/*
  SPELLROO singles search + filter
  ---------------------------------
  One index, loaded from templates/collection.search-index.liquid, drives both
  the search box and the filter sidebar on a singles collection page. Shopify's
  own storefront filters stop working above 5,000 products, so everything here
  runs in the browser.

  The index template only passes through the raw description fields
  (Set, Rarity, Card Type, Colour, Card Cost) and the in-stock printings.
  Every rule that turns those strings into filter values lives in this file.

  Layout of this file:
    1. core  - pure functions (parsing, matching, counting, URL state). No DOM.
               Exported for tests when loaded with require().
    2. ui    - index loading, sidebar, results, mobile drawer.
*/
(function (root) {
  'use strict';

  /* ================================================================== */
  /* 1. core                                                             */
  /* ================================================================== */

  var WUBRG = 'WUBRG';
  var MTG_TYPES = ['Creature', 'Legendary', 'Artifact', 'Enchantment', 'Sorcery', 'Land', 'Instant', 'Planeswalker', 'Battle', 'Kindred'];
  /* MTG print treatment, from the brackets in the title ("Thanos, The Mad Titan (Borderless) (0400) (MSH - 400)").
     A card can have two ("(Borderless) (Surge Foil)"); no treatment is Standard. 2026-10-21. */
  /* MTG keywords a card has itself: the keywords a line of its text starts with ("Flying, first
     strike", "Equip {2}", "Kicker R"), read in the index template (lower case there). A keyword a card
     hands out ("Equipped creature has haste") is inside a sentence and not counted. 2026-10-21. */
  var MTG_KEYWORDS = ['Flying', 'First strike', 'Double strike', 'Deathtouch', 'Defender', 'Flash', 'Haste', 'Hexproof', 'Indestructible',
    'Lifelink', 'Menace', 'Reach', 'Trample', 'Vigilance', 'Ward', 'Protection', 'Shroud', 'Prowess', 'Toxic', 'Infect',
    'Equip', 'Crew', 'Kicker', 'Flashback', 'Cycling', 'Convoke', 'Cascade', 'Changeling', 'Partner', 'Ninjutsu', 'Affinity', 'Storm', 'Annihilator', 'Undying', 'Persist'];
  var MTG_KW_BY_LOWER = {};
  MTG_KEYWORDS.forEach(function (k) { MTG_KW_BY_LOWER[k.toLowerCase()] = k; });
  var MTG_VERSIONS = ['Standard', 'Borderless', 'Extended Art', 'Showcase', 'Retro Frame', 'Future Sight', 'White Border', 'Special foil'];
  var MTG_RARITIES = ['Mythic', 'Rare', 'Uncommon', 'Common', 'Special', 'Promo', 'Land'];
  var MV_BUCKETS = ['0', '1', '2', '3', '4', '5', '6', '7', '8+'];
  var COLOURS = [
    { id: 'W', label: 'White' }, { id: 'U', label: 'Blue' }, { id: 'B', label: 'Black' },
    { id: 'R', label: 'Red' }, { id: 'G', label: 'Green' },
    { id: 'C', label: 'Colorless' }, { id: 'M', label: 'Multicolour' }
  ];
  var FINISHES = [{ id: 'Normal', label: 'Non-foil' }, { id: 'Foil', label: 'Foil' }];
  /* The theme's own "Sort by" values. The index comes in the collection's own order, which on
     the singles collections is the manual "Most popular" order ("Featured", sort_by=manual);
     it also stands in for "Most relevant". With a search query those two sort by how well the
     card name matches. "Best selling" has its own order, loaded when it is chosen
     (templates/collection.search-order.liquid) and kept on each card as it.best. */
  var SORTS = [
    'manual', 'most-relevant', 'best-selling', 'title-ascending', 'title-descending',
    'price-ascending', 'price-descending', 'created-ascending', 'created-descending'
  ];
  var LEGACY_SORTS = { best: '', 'price-asc': 'price-ascending', 'price-desc': 'price-descending', 'title-asc': 'title-ascending' };

  /* Other games (One Piece, Pokemon, Riftbound). MTG keeps the groups above; every other game lists its
     groups in PROFILES below. Each card gets it.g[groupId] = [values] when it is parsed, and
     matching, counting, chips and the URL all work from those lists. Rarity, set, finish and
     price use the same state as MTG. */
  var CONDITIONS = ['Near Mint', 'Lightly Played', 'Moderately Played', 'Heavily Played', 'Damaged'];
  var OP_COLOURS = ['Red', 'Green', 'Blue', 'Purple', 'Black', 'Yellow'];
  var OP_CATEGORIES = ['Leader', 'Character', 'Event', 'Stage', 'DON!!'];
  var OP_RARITIES = ['Leader', 'Common', 'Uncommon', 'Rare', 'Super Rare', 'Secret Rare', 'Treasure Rare', 'Special', 'Promo', 'DON!!'];
  var OP_VERSIONS = ['Standard', 'Alternate Art', 'Full Art', 'SP', 'Manga', 'Special foil', 'Reprint', 'Box Topper', 'Pre-release', 'Anniversary', 'Promo / event'];
  var PK_TYPES = ['Grass', 'Fire', 'Water', 'Lightning', 'Psychic', 'Fighting', 'Darkness', 'Metal', 'Fairy', 'Dragon', 'Colorless'];
  var PK_TYPE_ALIAS = { Dark: 'Darkness', Electric: 'Lightning', Steel: 'Metal', Colourless: 'Colorless' };
  var PK_MECHANICS = ['ex', 'EX', 'V', 'VMAX', 'VSTAR', 'GX', 'Mega', 'Radiant', 'BREAK', 'Prime', 'LV.X'];
  /* Riftbound: Domain is the Colour field ("Fury", "Fury, Chaos"), the Card Type is a list of
     words ("Champion Unit", "Signature Spell", "Unit;Gear"), Card Cost is the energy cost.
     Legends and runes carry a placeholder 0 there, so only units, spells and gear get a cost. */
  var RB_DOMAINS = ['Fury', 'Calm', 'Mind', 'Body', 'Chaos', 'Order'];
  var RB_TYPES = ['Unit', 'Champion', 'Spell', 'Gear', 'Legend', 'Rune', 'Battlefield', 'Signature', 'Token'];
  var RB_RARITIES = ['Common', 'Uncommon', 'Rare', 'Epic', 'Showcase', 'Promo'];
  var RB_VERSIONS = ['Standard', 'Alternate Art', 'Overnumbered'];
  /* Keywords a Riftbound card has itself: the [BRACKETED] words at the start of a line of its text
     (index template, 2026-10-21). One it hands out ("give a unit [ASSAULT]") is not counted.
     Action and Reaction are when a spell can be played. */
  /* Not in the list: words a line can also start with that are not a keyword of the card itself,
     like [EMPOWERED] (a condition: "[EMPOWERED] I have +1 Might") or [STUN] / [BURN] / [PREDICT]
     (something the card does). */
  var RB_KEYWORDS = ['Accelerate', 'Action', 'Ambush', 'Assault', 'Backline', 'Deathknell', 'Deflect', 'Empower', 'Equip', 'Flow', 'Ganking', 'Hidden', 'Hunt', 'Legion', 'Level', 'Quick-Draw', 'Reaction', 'Repeat', 'Shield', 'Tank', 'Temporary', 'Unique', 'Vision', 'Weaponmaster'];
  var RB_KEYWORD_ALIAS = { deatknell: 'Deathknell' };
  /* "QUICK-DRAW" -> "Quick-Draw"; '' for a word that is not in the list */
  function rbKeyword(k) {
    k = String(k || '').trim().toLowerCase().replace(/\d+$/, ''); /* "EQUIP1": a missing space */
    if (RB_KEYWORD_ALIAS[k]) return RB_KEYWORD_ALIAS[k];
    for (var i = 0; i < RB_KEYWORDS.length; i++) if (RB_KEYWORDS[i].toLowerCase() === k) return RB_KEYWORDS[i];
    return '';
  }
  /* Set list order, newest first (Star City Games 2026 schedule). A set not in this list and
     not a promo set is taken to be newer and goes on top. */
  var RB_SET_ORDER = ['Radiance', 'Vendetta', 'Unleashed', 'Spiritforged', 'Origins', 'Origins: Proving Grounds'];
  /* Disney Lorcana: Ink is the Colour field ("Amethyst", "Amber, Steel" for dual-ink cards), the
     Card Type starts with the kind ("Character", "Action - Song", "Item", "Location"), Card Cost is
     the ink cost. Printings are Normal, Holofoil (the standard foil) and Cold Foil (promos,
     Enchanted). Generated 2026-10-14. */
  var LOR_INKS = ['Amber', 'Amethyst', 'Emerald', 'Ruby', 'Sapphire', 'Steel'];
  var LOR_TYPES = ['Character', 'Action', 'Song', 'Item', 'Location'];
  var LOR_RARITIES = ['Common', 'Uncommon', 'Rare', 'Super Rare', 'Legendary', 'Epic', 'Enchanted', 'Iconic', 'Promo'];
  /* Keywords a card has itself (worked out from the card text in the index template, 2026-10-21).
     A keyword the card only hands out ("chosen character gains Rush") is not one of them. */
  var LOR_KEYWORDS = ['Alert', 'Bodyguard', 'Boost', 'Challenger', 'Evasive', 'Reckless', 'Resist', 'Rush', 'Shift', 'Singer', 'Sing Together', 'Support', 'Vanish', 'Ward'];
  /* Gundam Card Game: Colour is one of five (tokens and resources have none), Card Type is the
     kind ("Unit", "EX Resource"), Card Cost is the COST (not Lv: Gundam Epyon is Lv 8, COST 7,
     and our data says 7; checked against gundam-gcg.com 2026-10-30). Rarity is written with "+"
     for a parallel print ("LR+", "C++"); the filter shows the base rarity and the print under
     Version. Printings are Normal and Holofoil. Generated 2026-10-30. */
  var GD_COLOURS = ['Blue', 'Green', 'Red', 'White', 'Purple'];
  var GD_TYPES = ['Unit', 'Pilot', 'Command', 'Base', 'Resource', 'EX Base', 'EX Resource'];
  var GD_RARITIES = ['Common', 'Uncommon', 'Rare', 'Legend Rare', 'Promo'];
  var GD_RARITY_OF = { C: 'Common', U: 'Uncommon', UC: 'Uncommon', R: 'Rare', LR: 'Legend Rare', P: 'Promo' };
  var GD_VERSIONS = ['Standard', 'Parallel', 'Parallel ++', 'SP', 'Promo'];
  /* Keywords a card has itself: the [bracketed] ones a line of its text starts with ("[Blocker]
     (Rest this Unit ...)", "[Breach 3]"), read in the index template. One it only gets now and then
     ("While this is damaged, it gains [Suppression]", "[During Link] ... [First Strike]") is not
     counted. Burst is not a keyword in the rules but players look for it the same way. */
  var GD_KEYWORDS = ['Blocker', 'Breach', 'Burst', 'First Strike', 'High-Maneuver', 'Repair', 'Support', 'Suppression'];
  var GD_KEYWORD_ALIAS = { 'high manuever': 'High-Maneuver', 'high-manuever': 'High-Maneuver', 'high maneuver': 'High-Maneuver' };
  function gdKeyword(k) {
    k = String(k || '').trim().toLowerCase().replace(/\s*\d+$/, '');
    if (GD_KEYWORD_ALIAS[k]) return GD_KEYWORD_ALIAS[k];
    for (var i = 0; i < GD_KEYWORDS.length; i++) if (GD_KEYWORDS[i].toLowerCase() === k) return GD_KEYWORDS[i];
    return '';
  }
  function buckets(top) { var out = []; for (var i = 0; i < top; i++) out.push(String(i)); out.push(top + '+'); return out; }
  var PROFILES = {
    /* MTG keeps its own colour, mana value and type groups; these are the extra ones */
    mtg: {
      finishes: FINISHES,
      groups: [
        { id: 'mkw', label: 'Keyword', options: MTG_KEYWORDS, list: true, noun: 'keywords' },
        { id: 'mver', label: 'Version', options: MTG_VERSIONS },
        { id: 'cond', label: 'Condition', options: CONDITIONS }
      ]
    },
    op: {
      finishes: [{ id: 'Normal', label: 'Non-foil' }, { id: 'Foil', label: 'Foil' }],
      groups: [
        { id: 'oc', label: 'Colour', mode: true, options: OP_COLOURS.concat(['M']), names: { M: 'Multicolour' } },
        { id: 'ocat', label: 'Category', options: OP_CATEGORIES },
        { id: 'ocost', label: 'Cost', chip: 'Cost ', options: buckets(10) },
        { id: 'otr', label: 'Type', list: true },
        { id: 'ovar', label: 'Version', options: OP_VERSIONS },
        { id: 'cond', label: 'Condition', options: CONDITIONS }
      ]
    },
    rb: {
      finishes: [{ id: 'Normal', label: 'Non-foil' }, { id: 'Foil', label: 'Foil' }],
      groups: [
        { id: 'rd', label: 'Domain', mode: true, options: RB_DOMAINS.concat(['M']), names: { M: 'Dual domain' } },
        { id: 'rcost', label: 'Energy cost', chip: 'Energy ', options: buckets(8) },
        { id: 'rtype', label: 'Type', options: RB_TYPES },
        { id: 'rch', label: 'Champion', list: true, noun: 'champions' },
        { id: 'rkw', label: 'Keyword', options: RB_KEYWORDS, list: true, noun: 'keywords' },
        { id: 'rver', label: 'Version', options: RB_VERSIONS },
        { id: 'cond', label: 'Condition', options: CONDITIONS }
      ]
    },
    gd: {
      finishes: [{ id: 'Normal', label: 'Non-foil' }, { id: 'Holofoil', label: 'Foil' }],
      groups: [
        { id: 'gc', label: 'Colour', options: GD_COLOURS },
        { id: 'gcost', label: 'Cost', chip: 'Cost ', options: ['1', '2', '3', '4', '5', '6', '7', '8+'] },
        { id: 'gtype', label: 'Type', options: GD_TYPES },
        { id: 'gkw', label: 'Keyword', options: GD_KEYWORDS },
        { id: 'gver', label: 'Version', options: GD_VERSIONS },
        { id: 'cond', label: 'Condition', options: CONDITIONS }
      ]
    },
    lor: {
      finishes: [{ id: 'Normal', label: 'Non-foil' }, { id: 'Holofoil', label: 'Foil' }, { id: 'Cold Foil', label: 'Cold Foil' }],
      groups: [
        { id: 'li', label: 'Ink', mode: true, options: LOR_INKS.concat(['M']), names: { M: 'Dual ink' } },
        { id: 'lcost', label: 'Ink cost', chip: 'Cost ', options: buckets(10) },
        { id: 'ltype', label: 'Type', options: LOR_TYPES },
        { id: 'lkw', label: 'Keyword', options: LOR_KEYWORDS },
        { id: 'cond', label: 'Condition', options: CONDITIONS }
      ]
    },
    pokemon: {
      finishes: [{ id: 'Normal', label: 'Normal' }, { id: 'Holofoil', label: 'Holofoil' }, { id: 'Reverse Holofoil', label: 'Reverse Holofoil' }],
      groups: [
        { id: 'pcat', label: 'Card category', options: ['Pokemon', 'Trainer', 'Energy'] },
        { id: 'ptype', label: 'Pokemon type', options: PK_TYPES },
        { id: 'ptr', label: 'Trainer type', options: ['Supporter', 'Item', 'Pokemon Tool', 'Stadium'] },
        { id: 'pmech', label: 'Mechanic', options: PK_MECHANICS },
        { id: 'pret', label: 'Retreat cost', chip: 'Retreat ', options: buckets(4) },
        { id: 'cond', label: 'Condition', options: CONDITIONS }
      ]
    }
  };
  function profile(game) { return PROFILES[game] || null; }
  function groupsOf(game) { var p = profile(game); return p ? p.groups : []; }

  function decode(s) {
    return String(s == null ? '' : s)
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&quot;/g, '"')
      .replace(/&#0?39;|&apos;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .trim();
  }

  function norm(s) {
    return String(s || '')
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[‘’'`]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  /* Singles titles look like "Card Name (SET - 123) - Set Name - Rarity - Finish". */
  function cardName(t) {
    t = String(t || '');
    var i = t.indexOf(' (');
    return i > 0 ? t.slice(0, i) : t;
  }

  function cap(w) { return w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w; }

  /* "Legendary, Creature Legendary Creature — Human Villain"
     -> types ["Legendary","Creature"], line "Legendary Creature — Human Villain" */
  function parseType(raw) {
    raw = String(raw || '').trim();
    if (!raw) return { types: [], line: '' };
    var pieces = raw.split(/,\s*/);
    var last = pieces.pop();
    var sp = last.indexOf(' ');
    var first = sp < 0 ? last : last.slice(0, sp);
    var line = sp < 0 ? '' : last.slice(sp + 1).trim();
    var types = [];
    pieces.concat([first]).forEach(function (t) {
      t = cap(t.trim());
      if (t && types.indexOf(t) === -1) types.push(t);
    });
    return { types: types, line: line };
  }

  function sortColours(letters) {
    var out = '';
    for (var i = 0; i < WUBRG.length; i++) if (letters.indexOf(WUBRG[i]) !== -1) out += WUBRG[i];
    return out;
  }

  function costSymbols(cost) {
    var out = [], re = /\{([^}]+)\}/g, m;
    while ((m = re.exec(cost))) out.push(m[1]);
    return out;
  }

  function costColours(cost) {
    var letters = '';
    costSymbols(cost).forEach(function (sym) {
      sym.split('/').forEach(function (p) { if (p.length === 1 && WUBRG.indexOf(p) !== -1) letters += p; });
    });
    return sortColours(letters);
  }

  /* Mana value of the first face. X counts as 0, {2/G} as 2, any other symbol as 1. */
  function manaValue(cost) {
    var face = String(cost || '').split('//')[0];
    var total = 0;
    costSymbols(face).forEach(function (sym) {
      if (/^\d+$/.test(sym)) total += parseInt(sym, 10);
      else if (/^[XYZ]$/.test(sym)) total += 0;
      else if (/^\d+\//.test(sym)) total += parseInt(sym, 10);
      else total += 1;
    });
    return total;
  }

  /* Colour rule (see claude/singles-advanced-filter-plan-2026-10-11.md):
       1. has a cost with no coloured symbol      -> colourless ""
       2. otherwise the Colour field, in WUBRG order
       3. a land with no Colour                   -> colourless ""
       4. non-land, no cost, no Colour            -> unknown "?"
     So lands count as the colours they make. */
  var COLOUR_WORDS = { W: 'W', U: 'U', B: 'B', R: 'R', G: 'G', WHITE: 'W', BLUE: 'U', BLACK: 'B', RED: 'R', GREEN: 'G' };
  function colourOf(colourField, cost, isLand) {
    /* "B, U", and since 2026-10 also "Colorless" (whose letters must not count: it holds an R) */
    var ci = sortColours(String(colourField || '').toUpperCase().split(/[\s,;\/]+/).map(function (w) {
      return COLOUR_WORDS[w] || '';
    }).join(''));
    var hasCost = String(cost || '').trim() !== '';
    var cc = hasCost ? costColours(cost) : '';
    if (hasCost && cc === '') return '';
    if (ci !== '') return ci;
    if (isLand) return '';
    if (!hasCost) return '?';
    return cc;
  }

  function mvBucket(mv) { return mv == null ? null : (mv >= 8 ? '8+' : String(mv)); }

  function handleOf(url) {
    var p = String(url || '').split('?')[0].split('#')[0].split('/');
    return p[p.length - 1] || '';
  }

  /* The "(CODE - NUMBER)" part of a singles title: "Nico Robin (062) (OP09 - OP09-062) - ..."
     gives code "OP09", "Absol (CRZ:GG - GG16/GG70) - ..." gives "CRZ:GG". The name is what comes
     before it. */
  function titleParts(title) {
    var t = String(title || '');
    var m = t.match(/\(([^()]*?) - ([^()]*)\)/);
    /* no "(CODE - NUMBER)": keep the brackets before the first " - ", they can hold the print
       ("DON!! Card (Manga) (Alternate Art) (OP01) - Romance Dawn - DON!! - Foil") */
    if (!m) { var d = t.indexOf(' - '); return { name: (d > 0 ? t.slice(0, d) : t).trim(), code: '', num: '' }; }
    return { name: t.slice(0, m.index).trim(), code: m[1].trim(), num: m[2].trim() };
  }

  /* Set code for the set list ("Modern Horizons 3 (MH3)", "Romance Dawn (OP-01)"): the code most
     of the set's cards carry in the title, "(MH3 - 123)". Only the first word counts ("OP07 PRE"
     -> OP07). One Piece codes are written the way Bandai names the sets: OP07, OP-07 -> OP-07,
     EB02 -> EB-02 (cards carry both spellings); OP14-EB04, PRB-01 and OP-PR stay as they are.
     No code is added when the set name already has it as a word ("ST-14: Starter Deck 14 3D2Y"), and cards
     with no code are skipped. Checked against the live MTG, Riftbound and One Piece data on
     2026-10-24: every set has one main code. */
  function setCodes(items, game) {
    var tally = {};
    items.forEach(function (it) {
      if (!it.set) return;
      var c0 = titleParts(it.title).code.split(/\s+/)[0];
      /* Gundam writes some token prints as "(Premium Accessory Set - EXR-001)": a word, not a code */
      if (game === 'gd' && /[a-z]/.test(c0)) return;
      var c = c0.toUpperCase();
      if (!/^[A-Z0-9][A-Z0-9-]{1,9}$/.test(c)) return; /* MH3, MB2PC, 30A-P, OP14-EB04 */
      if (game === 'op') c = c.replace(/^([A-Z]+)(\d+)$/, '$1-$2');
      var t = tally[it.set] || (tally[it.set] = {});
      t[c] = (t[c] || 0) + 1;
    });
    var out = {};
    Object.keys(tally).forEach(function (s) {
      var best = '', n = 0;
      Object.keys(tally[s]).forEach(function (c) { if (tally[s][c] > n) { best = c; n = tally[s][c]; } });
      /* whole word, same case: "ST-14: ..." and "Magic 2010 (M10)" have it, "Radiance" (RAD) and
         "War of the Spark" (WAR) do not */
      var at = s.indexOf(best), had = false;
      while (at !== -1 && !had) {
        had = !/[A-Za-z0-9]/.test(s.charAt(at - 1) || '') && !/[A-Za-z0-9]/.test(s.charAt(at + best.length) || '');
        at = s.indexOf(best, at + 1);
      }
      if (!had) out[s] = best;
    });
    return out;
  }

  function costBucket(cost, top) {
    var c = String(cost == null ? '' : cost).trim();
    if (!/^\d+$/.test(c)) return null;
    var n = parseInt(c, 10);
    return n >= top ? top + '+' : String(n);
  }

  function uniq(list) { return list.filter(function (v, i, a) { return v && a.indexOf(v) === i; }); }

  /* One Piece version, from the brackets in the name and the code:
     "(Alternate Art)", "(Parallel)" -> Alternate Art; "(OP04 PRE - ...)" -> Pre-release;
     "(Judge Pack Vol. 7)" -> Promo / event. A bracket that is only a number or a character
     name ("Nico Robin (062)", "Galdino (Mr. 3)") is the standard card. */
  function opVersion(parts) {
    var tags = (parts.name.match(/\(([^()]*)\)/g) || []).map(function (s) { return s.slice(1, -1); }).join(' | ');
    var code = parts.code;
    /* the rarer prints first: a manga or SP card is also tagged "Parallel" or "Alternate Art" */
    if (/manga/i.test(tags)) return 'Manga';
    if (/\bSP\b/.test(tags)) return 'SP';
    if (/jolly roger foil|textured foil|pirate foil|\bgold\b/i.test(tags)) return 'Special foil';
    if (/full art/i.test(tags)) return 'Full Art';
    if (/alternate art|parallel/i.test(tags)) return 'Alternate Art';
    if (/reprint/i.test(tags) || /\bRE\b/.test(code)) return 'Reprint';
    if (/box topper/i.test(tags)) return 'Box Topper';
    if (/pre-?release/i.test(tags) || /\bPRE\b/.test(code)) return 'Pre-release';
    if (/anniversary/i.test(tags) || /\bANN\b/.test(code)) return 'Anniversary';
    if (/pack|collection|promo|event|championship|judge|winner|tournament|welcome|party|store|gift|binder|premium|regional|fest|battle kit|film|deck|celebration|sleeve/i.test(tags)) return 'Promo / event';
    return 'Standard';
  }

  /* One Piece Card Type is "<Category> <Type>;<Type>": "Character Straw Hat Crew;Supernovas". */
  function opGroups(it, r) {
    var colours = uniq(decode(r[9]).split(/[;,\/]/).map(function (c) { return cap(c.trim()); })
      .filter(function (c) { return OP_COLOURS.indexOf(c) !== -1; }));
    var type = decode(r[8]);
    var sp = type.indexOf(' ');
    var cat = (sp < 0 ? type : type.slice(0, sp)).trim();
    if (/^don/i.test(cat)) cat = 'DON!!';
    var cb = costBucket(decode(r[10]), 10);
    return {
      oc: colours.length > 1 ? colours.concat(['M']) : colours,
      ocat: OP_CATEGORIES.indexOf(cat) !== -1 ? [cat] : [],
      ocost: cb == null ? [] : [cb],
      otr: sp < 0 ? [] : uniq(type.slice(sp + 1).split(';').map(function (s) { return s.trim(); })),
      ovar: [opVersion(titleParts(it.title))]
    };
  }

  function rbGroups(it, r) {
    var domains = uniq(decode(r[9]).split(/[;,\/]/).map(function (c) { return cap(c.trim()); })
      .filter(function (c) { return RB_DOMAINS.indexOf(c) !== -1; }));
    var types = uniq(decode(r[8]).split(/[\s;,\/]+/).map(cap)
      .filter(function (w) { return RB_TYPES.indexOf(w) !== -1; }));
    var costed = types.indexOf('Unit') !== -1 || types.indexOf('Spell') !== -1 || types.indexOf('Gear') !== -1;
    var cb = costed ? costBucket(decode(r[10]), 8) : null;
    /* runes carry no Domain: "Chaos Rune (R05a)" is a Chaos card */
    var rune = /^(Fury|Calm|Mind|Body|Chaos|Order) Rune\b/.exec(it.title);
    if (!domains.length && rune) domains = [rune[1]];
    var tags = (titleParts(it.title).name.match(/\(([^()]*)\)/g) || []).join(' ');
    /* a legend or champion unit is named after its champion: "Kai'Sa, Survivor", "Rumble - Scrapper".
       Signature cards get theirs from the card list (rbEnrich). */
    var champ = '';
    if (types.indexOf('Legend') !== -1 || types.indexOf('Champion') !== -1) champ = rbBaseName(it.title).split(/, | - /)[0].trim();
    var ver = /overnumbered/i.test(tags) ? 'Overnumbered' : (/alternate art|alt art/i.test(tags) ? 'Alternate Art' : 'Standard');
    return {
      rd: domains.length > 1 ? domains.concat(['M']) : domains,
      rcost: cb == null ? [] : [cb],
      rtype: types,
      rch: champ ? [champ] : [],
      rkw: uniq(String(r[15] || '').split('|').map(rbKeyword)),
      rver: [ver]
    };
  }

  /* Riftbound: an Alternate Art or Overnumbered print sometimes comes without its Domain or Card
     Type (89 and 7 cards on 2026-10-21). Every print of a card has the same ones, so they are
     taken from another print with the same name. */
  function rbBaseName(title) { return titleParts(title).name.replace(/\([^()]*\)/g, ' ').replace(/\s+/g, ' ').trim(); }
  function rbTwinKey(it) { return norm(rbBaseName(it.title)); }

  /* assets/sr-rb-cards.json: every Riftbound card by name (normalised) -> "Domain,Domain|Champion",
     built from the card list in github.com/ChristianIvicevic/riftboundfaq (sources/card-catalog.json,
     2026-10-21, 1,096 cards up to Radiance). It fills a Domain our product data is missing (223 of 225
     on 2026-10-21) and gives signature cards their champion. A legend is listed by its title
     ("Daughter of the Void") and some champions by a short name ("Yi, Honed"), so a few names are tried. */
  function rbCardKeys(title) {
    var base = rbBaseName(title);
    var tags = (titleParts(title).name.match(/\(([^()]*)\)/g) || []).map(function (t) { return t.slice(1, -1); })
      .filter(function (t) { return !/alternate art|overnumbered|^R\d|^\d/i.test(t); }).join(' ');
    var keys = [norm(base)];
    var m = base.match(/^(.*?)(?:, | - )(.*)$/);
    if (m) keys.push(norm(m[2]), norm(m[2] + ' ' + tags), norm(m[1].split(' ').slice(1).join(' ') + ' ' + m[2]));
    return keys.filter(Boolean);
  }
  function rbEnrich(items, cards) {
    if (!cards) return;
    items.forEach(function (it) {
      if (!it.g) return;
      var keys = rbCardKeys(it.title), hit = null;
      for (var i = 0; i < keys.length && hit == null; i++) if (Object.prototype.hasOwnProperty.call(cards, keys[i])) hit = cards[keys[i]];
      if (hit == null) return;
      var parts = String(hit).split('|');
      if (!it.g.rd.length) {
        var d = uniq(parts[0].split(',').filter(function (x) { return RB_DOMAINS.indexOf(x) !== -1; }));
        it.g.rd = d.length > 1 ? d.concat(['M']) : d;
      }
      if (parts[1]) it.g.rch = [parts[1]];
    });
  }
  function fillTwins(items, game) {
    if (game !== 'rb') return;
    var best = new Map();
    /* runs after rbEnrich too, so a card the list does not know still gets a twin's champion */
    items.forEach(function (it) {
      if (!it.g || !it.g.rd.length || !it.g.rtype.length) return;
      var k = rbTwinKey(it);
      if (!best.has(k)) best.set(k, it);
    });
    items.forEach(function (it) {
      if (!it.g || (it.g.rd.length && it.g.rtype.length)) return;
      var tw = best.get(rbTwinKey(it));
      if (!tw) return;
      if (!it.g.rd.length) it.g.rd = tw.g.rd.slice();
      if (!it.g.rtype.length) it.g.rtype = tw.g.rtype.slice();
      if (!it.g.rcost.length) it.g.rcost = tw.g.rcost.slice();
      if (!it.g.rch.length) it.g.rch = tw.g.rch.slice();
    });
  }

  /* Set list order where a game has one (Riftbound): newest first, promo sets last. */
  function rbSetRank(name) {
    var i = RB_SET_ORDER.indexOf(name);
    if (i !== -1) return i;
    return !name || /promo/i.test(name) ? 1000 : -1;
  }

  function mtgKeywords(raw) {
    return uniq(String(raw || '').split('|').map(function (k) { return MTG_KW_BY_LOWER[k.trim().toLowerCase()] || ''; }));
  }

  function mtgVersions(title) {
    var tags = (titleParts(title).name.match(/\(([^()]*)\)/g) || []).join(' ');
    var out = [];
    if (/borderless/i.test(tags)) out.push('Borderless');
    if (/extended art/i.test(tags)) out.push('Extended Art');
    if (/showcase/i.test(tags)) out.push('Showcase');
    if (/retro frame/i.test(tags)) out.push('Retro Frame');
    if (/future sight/i.test(tags)) out.push('Future Sight');
    if (/white border/i.test(tags)) out.push('White Border');
    if (/(surge|etched|textured|galaxy|halo|rainbow|ripple|first-place|chocobo track|silver scroll|confetti|serialized|step-and-compleat|oil slick|neon ink|raised) ?foil|foil etched/i.test(tags)) out.push('Special foil');
    return out.length ? out : ['Standard'];
  }

  function lorGroups(it, r) {
    var inks = uniq(decode(r[9]).split(/[;,\/]/).map(function (c) { return cap(c.trim()); })
      .filter(function (c) { return LOR_INKS.indexOf(c) !== -1; }));
    var types = uniq(decode(r[8]).split(/[\s;,\/\-]+/).map(cap)
      .filter(function (w) { return LOR_TYPES.indexOf(w) !== -1; }));
    var cb = costBucket(decode(r[10]), 10);
    /* set number from "(14 - 17/204)" for the set list order; promo codes ("DLPC") have none */
    var no = parseInt(titleParts(it.title).code, 10);
    it.setNo = isFinite(no) ? no : -1;
    return {
      li: inks.length > 1 ? inks.concat(['M']) : inks,
      lcost: cb == null ? [] : [cb],
      ltype: types,
      lkw: String(r[15] || '').split('|').filter(function (k) { return LOR_KEYWORDS.indexOf(k) !== -1; })
    };
  }

  /* Gundam: the index gives Set, Rarity ("LR+"), Card Type, Colour, Card Cost and, as r[15], the
     card's own keywords as written ("Blocker|Breach 3"). it.rarity becomes the base rarity name,
     so the Rarity group and its chips read "Legend Rare", not "LR+". */
  function gdGroups(it, r) {
    var raw = decode(r[7]).replace(/\s+/g, '').toUpperCase();
    var plus = (raw.match(/\++$/) || [''])[0].length;
    var base = raw.replace(/\++$/, '');
    it.rarity = GD_RARITY_OF[base] || decode(r[7]);
    var colour = cap(decode(r[9]).trim());
    var type = decode(r[8]).trim();
    var cb = costBucket(decode(r[10]), 8);
    var parts = titleParts(it.title);
    var tags = (parts.name.match(/\(([^()]*)\)/g) || []).map(function (t) { return t.slice(1, -1); });
    var ver = [];
    if (plus >= 2) ver.push('Parallel ++'); else if (plus === 1) ver.push('Parallel');
    if (tags.indexOf('SP') !== -1) ver.push('SP');
    if (base === 'P' || /promo/i.test(it.set) || /^GCG-PR$/i.test(parts.code)) ver.push('Promo');
    return {
      gc: GD_COLOURS.indexOf(colour) !== -1 ? [colour] : [],
      gcost: cb == null || cb === '0' ? [] : [cb],
      gtype: GD_TYPES.indexOf(type) !== -1 ? [type] : [],
      gkw: uniq(String(r[15] || '').split('|').map(gdKeyword)),
      gver: ver.length ? ver : ['Standard']
    };
  }

  function lorFinish(f) {
    if (/cold/i.test(f)) return 'Cold Foil';
    if (/foil|holo/i.test(f)) return 'Holofoil';
    return 'Normal';
  }

  /* Pokemon Card Type holds the Pokemon's type ("Water", "Lightning Water"), the Trainer kind
     ("Trainer - Supporter", "Item") or the Energy kind ("Basic Fire Energy"). Card Cost is the
     retreat cost (checked against "Retreat Cost: N" in the card text: 791 of 791 agree). */
  function pkGroups(it, r) {
    var type = decode(r[8]);
    var t = type.toLowerCase();
    var name = titleParts(it.title).name;
    var types = uniq(type.split(/[\s,;\/]+/).map(function (w) { w = cap(w); return PK_TYPE_ALIAS[w] || w; })
      .filter(function (w) { return PK_TYPES.indexOf(w) !== -1; }));
    var cat = '';
    if (/energy/.test(t) || (!type && /\benergy\b/i.test(name))) cat = 'Energy';
    else if (/trainer|supporter|item|stadium|tool/.test(t)) cat = 'Trainer';
    else if (types.length) cat = 'Pokemon';
    var tr = '';
    if (cat === 'Trainer') {
      if (/supporter/.test(t)) tr = 'Supporter';
      else if (/stadium/.test(t)) tr = 'Stadium';
      else if (/tool/.test(t)) tr = 'Pokemon Tool';
      else if (/item/.test(t)) tr = 'Item';
    }
    var mech = [];
    if (/\bex\b/.test(name)) mech.push('ex');
    if (/\bEX\b/.test(name)) mech.push('EX');
    if (/\bVMAX\b/.test(name)) mech.push('VMAX');
    else if (/\bVSTAR\b/.test(name)) mech.push('VSTAR');
    else if (/\bV\b/.test(name)) mech.push('V');
    if (/\bGX\b/.test(name)) mech.push('GX');
    if (/^(Mega|M) /.test(name)) mech.push('Mega');
    if (/^Radiant /.test(name)) mech.push('Radiant');
    if (/\bBREAK\b/.test(name)) mech.push('BREAK');
    if (/\bPrime\b/.test(name)) mech.push('Prime');
    if (/\bLV\.?\s?X\b/i.test(name)) mech.push('LV.X');
    var rb = cat === 'Pokemon' ? costBucket(decode(r[10]), 4) : null;
    return {
      pcat: cat ? [cat] : [],
      ptype: cat === 'Trainer' ? [] : types,
      ptr: tr ? [tr] : [],
      pmech: mech,
      pret: rb == null ? [] : [rb]
    };
  }

  function pkFinish(f) {
    if (/reverse/i.test(f)) return 'Reverse Holofoil';
    if (/holo/i.test(f)) return 'Holofoil';
    return 'Normal';
  }

  function condName(c) {
    c = String(c || '').trim().toLowerCase();
    for (var i = 0; i < CONDITIONS.length; i++) if (CONDITIONS[i].toLowerCase() === c) return CONDITIONS[i];
    return '';
  }

  /* Names written with a hyphen or dots are also found typed as one word:
     "Ho-Oh" by "hooh", "Chi-Yu" by "chiyu", "Monkey.D.Luffy" by "monkeydluffy". */
  function joinedWords(name) {
    var m = String(name || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').match(/[A-Za-z0-9]+(?:[-.][A-Za-z0-9]+)+/g);
    return m ? m.map(function (w) { return w.replace(/[-.]/g, ''); }).join(' ') : '';
  }

  /* Index row: [url, title, price_min, price_varies, available, image,
                 set, rarity, card type, colour, card cost, printings("Normal|Foil"), created (unix seconds),
                 conditions in stock ("Near Mint|Lightly Played"),
                 in-stock variant prices by printing ("Normal:3143|Foil:3225|Normal:2900", cents)] */
  function parseItem(r, order, game) {
    var title = String(r[1] || '');
    var set = decode(r[6]);
    var rarity = decode(r[7]);
    var rawType = decode(r[8]);
    var cost = decode(r[10]);
    var tp = parseType(rawType);
    var isLand = tp.types.indexOf('Land') !== -1;
    var hasCost = cost !== '';
    var mv = hasCost ? manaValue(cost) : (isLand ? 0 : null);
    var fin = r[11] ? String(r[11]).split('|').filter(Boolean) : [];
    var hay = norm(title + ' ' + set + ' ' + rawType + ' ' + joinedWords(cardName(title)));
    var it = {
      url: r[0], handle: handleOf(r[0]), title: title,
      name: norm(cardName(title)), tsort: title.toLowerCase(),
      hay: hay,
      hayW: ' ' + hay,
      price: Number(r[2]) || 0, varies: !!r[3], available: r[4] !== false, image: r[5] || null,
      order: order,
      set: set, rarity: rarity, types: tp.types, typeLine: tp.line,
      col: colourOf(decode(r[9]), cost, isLand),
      mv: mv, mvb: mvBucket(mv), fin: fin,
      created: Number(r[12]) || 0
    };
    if (game === 'mtg' || game === 'op' || game === 'pokemon' || game === 'rb' || game === 'lor' || game === 'gd') {
      it.g = game === 'mtg' ? { mkw: mtgKeywords(r[15]), mver: mtgVersions(title) } : game === 'gd' ? gdGroups(it, r) : game === 'op' ? opGroups(it, r) : (game === 'rb' ? rbGroups(it, r) : (game === 'lor' ? lorGroups(it, r) : pkGroups(it, r)));
      it.g.cond = uniq(String(r[13] || '').split('|').map(condName));
      if (game === 'pokemon') it.fin = uniq(it.fin.map(pkFinish));
      if (game === 'lor') it.fin = uniq(it.fin.map(lorFinish));
    }
    it.vp = variantPrices(r[14], game);
    return it;
  }

  /* "Normal~Near Mint:3143|Foil~Near Mint:3225|Normal~Lightly Played:2900" ->
     [{ f: 'Normal', c: 'Near Mint', p: 3143 }, ...], printings named the same way as it.fin
     (Pokemon and Lorcana printings are renamed) and conditions as in it.g.cond. null when the
     index has no prices (an index cached before they were added). */
  function variantPrices(raw, game) {
    if (!raw) return null;
    var out = [];
    String(raw).split('|').forEach(function (part) {
      var colon = part.lastIndexOf(':');
      if (colon <= 0) return;
      var cents = Number(part.slice(colon + 1));
      if (!isFinite(cents)) return;
      var key = part.slice(0, colon);
      var tilde = key.indexOf('~');
      var f = tilde >= 0 ? key.slice(0, tilde) : key;
      var c = tilde >= 0 ? condName(key.slice(tilde + 1)) : '';
      if (game === 'pokemon') f = pkFinish(f);
      else if (game === 'lor') f = lorFinish(f);
      out.push({ f: f, c: c, p: cents });
    });
    return out.length ? out : null;
  }

  /* The price that goes with what the shopper filtered on (Finish and, where the game has it,
     Condition): with Finish = Foil, a card that has a $2 Normal and a $5.25 Foil is a $5.25 card
     (price filter, price sort and the price shown). With neither filter on it is the card's
     lowest in-stock price, as before. */
  function effPrice(it, state) {
    var fins = state.fins || [];
    var conds = gsel(state, 'cond');
    if ((!fins.length && !conds.length) || !it.vp) return { price: it.price, varies: it.varies, byFinish: false };
    var min = null, max = null;
    for (var i = 0; i < it.vp.length; i++) {
      var v = it.vp[i];
      if (fins.length && fins.indexOf(v.f) === -1) continue;
      if (conds.length && v.c && conds.indexOf(v.c) === -1) continue;
      if (min == null || v.p < min) min = v.p;
      if (max == null || v.p > max) max = v.p;
    }
    if (min == null) return { price: it.price, varies: it.varies, byFinish: false };
    return { price: min, varies: max > min, byFinish: true };
  }
  function priceFor(it, state) { return it.vp && (state.fins.length || gsel(state, 'cond').length) ? effPrice(it, state).price : it.price; }

  function emptyState(game) {
    return {
      game: game || '', g: {}, gm: {},
      q: '', colours: [], colourMode: 'any', sets: [], rarities: [],
      types: [], typeMode: 'any', mvs: [], fins: [], stock: [], pmin: null, pmax: null, sort: '', page: 1
    };
  }

  /* Availability ("in" / "out"): every game has it, as the first group of the sidebar.
     A card is in stock when any of its variants can be bought (the index's `available`). */
  var STOCK = [{ id: 'in', label: 'In stock' }, { id: 'out', label: 'Out of stock' }];
  function stockOf(it) { return it.available ? 'in' : 'out'; }

  /* selected values of a profile group (always an array) */
  function gsel(state, id) { return (state.g && state.g[id]) || []; }
  function gActive(state) {
    var gs = groupsOf(state.game);
    for (var i = 0; i < gs.length; i++) if (gsel(state, gs[i].id).length) return true;
    return false;
  }

  function queryTokens(q) { return norm(q).split(' ').filter(Boolean); }
  /* Each word of the search has to be the start of a word on the card, so "heal" finds
     "Healing" but not "Spheal", and "ring" finds "Sol Ring" but not "during". */
  function wordTokens(q) { return queryTokens(q).map(function (t) { return ' ' + t; }); }
  function hasQuery(state) { return norm(state.q).length >= 2; }

  function isActive(state) {
    return hasQuery(state) || state.colours.length > 0 || state.sets.length > 0 || state.rarities.length > 0 ||
      state.types.length > 0 || state.mvs.length > 0 || state.fins.length > 0 || state.stock.length > 0 ||
      state.pmin != null || state.pmax != null ||
      gActive(state);
  }

  function activeGroupCount(state) {
    var n = 0;
    if (state.stock.length) n++;
    if (state.colours.length) n++;
    if (state.sets.length) n++;
    if (state.rarities.length) n++;
    if (state.types.length) n++;
    if (state.mvs.length) n++;
    if (state.fins.length) n++;
    if (state.pmin != null || state.pmax != null) n++;
    groupsOf(state.game).forEach(function (g) { if (gsel(state, g.id).length) n++; });
    return n;
  }

  function colourTokenMatch(it, token) {
    if (token === 'C') return it.col === '';
    if (token === 'M') return it.col.length > 1;
    return it.col.indexOf(token) !== -1;
  }

  function matchColour(it, colours, mode) {
    if (!colours.length) return true;
    if (it.col === '?') return false;
    var i;
    if (mode === 'all') {
      for (i = 0; i < colours.length; i++) if (!colourTokenMatch(it, colours[i])) return false;
      return true;
    }
    for (i = 0; i < colours.length; i++) if (colourTokenMatch(it, colours[i])) return true;
    return false;
  }

  function matchType(it, types, mode) {
    if (!types.length) return true;
    var i;
    if (mode === 'all') {
      for (i = 0; i < types.length; i++) if (it.types.indexOf(types[i]) === -1) return false;
      return true;
    }
    for (i = 0; i < types.length; i++) if (it.types.indexOf(types[i]) !== -1) return true;
    return false;
  }

  function matchFin(it, fins) {
    if (!fins.length) return true;
    for (var i = 0; i < fins.length; i++) if (it.fin.indexOf(fins[i]) !== -1) return true;
    return false;
  }

  /* skip = the group being counted, so its own selection does not hide its other options. */
  function matches(it, state, tokens, skip) {
    var i;
    var hay = it.fullW || it.hayW;
    for (i = 0; i < tokens.length; i++) if (hay.indexOf(tokens[i]) === -1) return false;
    if (state.pmin != null || state.pmax != null) {
      var pr = priceFor(it, state);
      if (state.pmin != null && pr < state.pmin) return false;
      if (state.pmax != null && pr > state.pmax) return false;
    }
    if (skip !== 'stock' && state.stock.length && state.stock.indexOf(stockOf(it)) === -1) return false;
    if (skip !== 'colour' && !matchColour(it, state.colours, state.colourMode)) return false;
    if (skip !== 'set' && state.sets.length && state.sets.indexOf(it.set) === -1) return false;
    if (skip !== 'rarity' && state.rarities.length && state.rarities.indexOf(it.rarity) === -1) return false;
    if (skip !== 'type' && !matchType(it, state.types, state.typeMode)) return false;
    if (skip !== 'mv' && state.mvs.length && (it.mvb == null || state.mvs.indexOf(it.mvb) === -1)) return false;
    if (skip !== 'fin' && !matchFin(it, state.fins)) return false;
    var gs = groupsOf(state.game);
    for (i = 0; i < gs.length; i++) {
      var id = gs[i].id;
      if (skip === id) continue;
      var sel = gsel(state, id);
      if (sel.length && !matchList(it.g ? it.g[id] : null, sel, state.gm && state.gm[id])) return false;
    }
    return true;
  }

  /* any: the card has one of the chosen values; all: it has every one of them */
  function matchList(vals, sel, mode) {
    vals = vals || [];
    var i;
    if (mode === 'all') {
      for (i = 0; i < sel.length; i++) if (vals.indexOf(sel[i]) === -1) return false;
      return true;
    }
    for (i = 0; i < sel.length; i++) if (vals.indexOf(sel[i]) !== -1) return true;
    return false;
  }

  function relevance(it, phrase) {
    var n = it.name;
    if (n.indexOf(phrase) === 0) return 0;
    if ((' ' + n).indexOf(' ' + phrase) !== -1) return 1;
    if (n.indexOf(phrase) !== -1) return 2;
    if ((' ' + it.hay).indexOf(' ' + phrase) !== -1) return 3;
    var toks = phrase.split(' ');
    for (var i = 0; i < toks.length; i++) {
      if (it.hayW.indexOf(' ' + toks[i]) === -1) {
        /* needs the card text: the whole phrase in it first, then the words anywhere */
        return it.text && (' ' + it.text).indexOf(' ' + phrase) !== -1 ? 5 : 6;
      }
    }
    return 4;
  }

  /* Card text (rules / abilities) from templates/collection.search-text.liquid, added to an
     item once it has loaded, and searched together with the name, set and type line.
     Reminder text in brackets is left out: "(It can't be the target of spells ...)" would
     otherwise match searches for words the card does not really use. */
  function setText(it, text) {
    it.text = norm(decode(text).replace(/\([^)]*\)/g, ' '));
    it.full = it.text ? it.hay + ' ' + it.text : null;
    it.fullW = it.full ? ' ' + it.full : null;
  }

  function apply(items, state) {
    var tokens = hasQuery(state) ? queryTokens(state.q) : [];
    var words = hasQuery(state) ? wordTokens(state.q) : [];
    var phrase = tokens.join(' ');
    var out = [];
    for (var i = 0; i < items.length; i++) if (matches(items[i], state, words, null)) out.push(items[i]);
    var sort = state.sort || '';
    function byTitle(a, b) { return a.tsort < b.tsort ? -1 : (a.tsort > b.tsort ? 1 : 0); }
    var pk = null;
    if (sort === 'price-ascending' || sort === 'price-descending') {
      pk = new Map();
      out.forEach(function (it) { pk.set(it, priceFor(it, state)); });
    }
    if (sort === 'price-ascending') out.sort(function (a, b) { return pk.get(a) - pk.get(b) || a.order - b.order; });
    else if (sort === 'price-descending') out.sort(function (a, b) { return pk.get(b) - pk.get(a) || a.order - b.order; });
    else if (sort === 'title-ascending') out.sort(function (a, b) { return byTitle(a, b) || a.order - b.order; });
    else if (sort === 'title-descending') out.sort(function (a, b) { return byTitle(b, a) || a.order - b.order; });
    else if (sort === 'created-ascending') out.sort(function (a, b) { return a.created - b.created || a.order - b.order; });
    else if (sort === 'created-descending') out.sort(function (a, b) { return b.created - a.created || a.order - b.order; });
    else if (sort === 'best-selling' && items.length && items[0].best != null) {
      /* it.best is set on every card once the order has loaded; cards added since it was
         loaded have Infinity and go last, in collection order */
      out.sort(function (a, b) { return a.best - b.best || a.order - b.order || 0; });
    }
    else if (tokens.length) {
      var score = new Map();
      out.forEach(function (it) { score.set(it, relevance(it, phrase)); });
      out.sort(function (a, b) { return score.get(a) - score.get(b) || a.order - b.order; });
    } else out.sort(function (a, b) { return a.order - b.order; });
    return out;
  }

  /* Counts for every option of every group. Each group is counted with the
     other groups applied, so a shopper sees what ticking one more box gives. */
  function facets(items, state) {
    var tokens = hasQuery(state) ? wordTokens(state.q) : [];
    var f = { stock: { in: 0, out: 0 }, colour: {}, set: {}, rarity: {}, type: {}, mv: {}, fin: {}, g: {}, setCount: 0 };
    var prof = profile(state.game);
    var gs = prof ? prof.groups : [];
    COLOURS.forEach(function (c) { f.colour[c.id] = 0; });
    MTG_TYPES.forEach(function (t) { f.type[t] = 0; });
    MV_BUCKETS.forEach(function (b) { f.mv[b] = 0; });
    (prof ? prof.finishes : FINISHES).forEach(function (x) { f.fin[x.id] = 0; });
    gs.forEach(function (g) { f.g[g.id] = {}; (g.options || []).forEach(function (o) { f.g[g.id][o] = 0; }); });
    var allColours = state.colourMode === 'all' ? state.colours : [];
    var allTypes = state.typeMode === 'all' ? state.types : [];
    var i, j, it;
    for (i = 0; i < items.length; i++) {
      it = items[i];
      if (matches(it, state, tokens, 'stock')) f.stock[stockOf(it)]++;
      if (matches(it, state, tokens, 'colour') && it.col !== '?') {
        for (j = 0; j < COLOURS.length; j++) {
          var cid = COLOURS[j].id;
          if (!colourTokenMatch(it, cid)) continue;
          if (allColours.length && !matchColour(it, allColours, 'all')) continue;
          f.colour[cid]++;
        }
      }
      if (matches(it, state, tokens, 'set')) f.set[it.set] = (f.set[it.set] || 0) + 1;
      if (matches(it, state, tokens, 'rarity')) f.rarity[it.rarity] = (f.rarity[it.rarity] || 0) + 1;
      if (matches(it, state, tokens, 'type')) {
        if (!allTypes.length || matchType(it, allTypes, 'all')) {
          for (j = 0; j < it.types.length; j++) if (f.type[it.types[j]] != null) f.type[it.types[j]]++;
        }
      }
      if (it.mvb != null && matches(it, state, tokens, 'mv')) f.mv[it.mvb]++;
      if (matches(it, state, tokens, 'fin')) for (j = 0; j < it.fin.length; j++) if (f.fin[it.fin[j]] != null) f.fin[it.fin[j]]++;
      for (var k = 0; k < gs.length; k++) {
        var gid = gs[k].id;
        var vals = it.g ? it.g[gid] : null;
        if (!vals || !vals.length || !matches(it, state, tokens, gid)) continue;
        /* "all" mode: count what adding one more value gives, like the MTG colours */
        if (state.gm && state.gm[gid] === 'all' && gsel(state, gid).length && !matchList(vals, gsel(state, gid), 'all')) continue;
        var cnt = f.g[gid];
        for (j = 0; j < vals.length; j++) {
          if (gs[k].options && cnt[vals[j]] == null) continue;
          cnt[vals[j]] = (cnt[vals[j]] || 0) + 1;
        }
      }
    }
    f.setCount = Object.keys(f.set).length;
    return f;
  }

  /* ---- URL state: #srf&c=UB&cm=all&t=Land ... ; the old #search=foo still works ---- */
  function listParam(v) { return v ? v.split('|').filter(Boolean) : []; }

  function encodeState(state) {
    if (!isActive(state)) return '';
    var p = [];
    function add(k, v) { p.push(k + '=' + encodeURIComponent(v)); }
    if (hasQuery(state)) add('q', state.q.trim());
    if (state.stock.length) add('av', state.stock.join('|'));
    if (state.colours.length) add('c', state.colours.join(''));
    if (state.colours.length && state.colourMode === 'all') add('cm', 'all');
    if (state.sets.length) add('s', state.sets.join('|'));
    if (state.rarities.length) add('r', state.rarities.join('|'));
    if (state.types.length) add('t', state.types.join('|'));
    if (state.types.length && state.typeMode === 'all') add('tm', 'all');
    if (state.mvs.length) add('mv', state.mvs.join('|'));
    if (state.fins.length) add('f', state.fins.join('|'));
    if (state.pmin != null) add('pmin', state.pmin);
    if (state.pmax != null) add('pmax', state.pmax);
    groupsOf(state.game).forEach(function (g) {
      var sel = gsel(state, g.id);
      if (!sel.length) return;
      add(g.id, sel.join('|'));
      if (g.mode && state.gm && state.gm[g.id] === 'all') add(g.id + 'm', 'all');
    });
    if (state.sort) add('o', state.sort);
    if (state.page > 1) add('pg', state.page);
    return p.length ? '#srf&' + p.join('&') : '';
  }

  function decodeState(hash, game) {
    var s = emptyState(game);
    hash = String(hash || '');
    var legacy = hash.match(/^#search=(.*)$/);
    if (legacy) { try { s.q = decodeURIComponent(legacy[1]); } catch (e) {} return s; }
    if (hash.indexOf('#srf') !== 0) return s;
    var kv = {};
    hash.slice(4).split('&').forEach(function (pair) {
      if (!pair) return;
      var i = pair.indexOf('=');
      if (i < 0) return;
      try { kv[pair.slice(0, i)] = decodeURIComponent(pair.slice(i + 1).replace(/\+/g, ' ')); } catch (e) {}
    });
    var ids = COLOURS.map(function (c) { return c.id; });
    if (kv.q) s.q = kv.q;
    if (kv.c) s.colours = kv.c.toUpperCase().split('').filter(function (c, i, a) { return ids.indexOf(c) !== -1 && a.indexOf(c) === i; });
    if (kv.cm === 'all') s.colourMode = 'all';
    s.sets = listParam(kv.s);
    s.rarities = listParam(kv.r);
    s.types = listParam(kv.t);
    if (kv.tm === 'all') s.typeMode = 'all';
    s.mvs = listParam(kv.mv).filter(function (b) { return MV_BUCKETS.indexOf(b) !== -1; });
    s.fins = listParam(kv.f);
    var stockIds = STOCK.map(function (x) { return x.id; });
    s.stock = listParam(kv.av).filter(function (v, i, a) { return stockIds.indexOf(v) !== -1 && a.indexOf(v) === i; });
    var n;
    if (kv.pmin != null && kv.pmin !== '') { n = parseInt(kv.pmin, 10); if (isFinite(n) && n >= 0) s.pmin = n; }
    if (kv.pmax != null && kv.pmax !== '') { n = parseInt(kv.pmax, 10); if (isFinite(n) && n >= 0) s.pmax = n; }
    groupsOf(game).forEach(function (g) {
      var sel = listParam(kv[g.id]);
      if (g.options) sel = sel.filter(function (v) { return g.options.indexOf(v) !== -1; });
      if (sel.length) s.g[g.id] = sel;
      if (g.mode && kv[g.id + 'm'] === 'all') s.gm[g.id] = 'all';
    });
    if (kv.o && SORTS.indexOf(kv.o) !== -1) s.sort = kv.o;
    else if (kv.o && LEGACY_SORTS[kv.o] != null) s.sort = LEGACY_SORTS[kv.o];
    if (kv.pg) { n = parseInt(kv.pg, 10); if (isFinite(n) && n > 1) s.page = n; }
    return s;
  }

  var core = {
    WUBRG: WUBRG, MTG_TYPES: MTG_TYPES, MTG_RARITIES: MTG_RARITIES, MV_BUCKETS: MV_BUCKETS,
    COLOURS: COLOURS, FINISHES: FINISHES, SORTS: SORTS, STOCK: STOCK, stockOf: stockOf,
    decode: decode, norm: norm, cardName: cardName, parseType: parseType, costColours: costColours,
    manaValue: manaValue, colourOf: colourOf, parseItem: parseItem, fillTwins: fillTwins, rbEnrich: rbEnrich, rbCardKeys: rbCardKeys, mtgKeywords: mtgKeywords, rbSetRank: rbSetRank, mtgVersions: mtgVersions, emptyState: emptyState,
    isActive: isActive, hasQuery: hasQuery, activeGroupCount: activeGroupCount, matches: matches, apply: apply, facets: facets,
    encodeState: encodeState, decodeState: decodeState, handleOf: handleOf, setText: setText,
    PROFILES: PROFILES, CONDITIONS: CONDITIONS, OP_RARITIES: OP_RARITIES, RB_RARITIES: RB_RARITIES, LOR_RARITIES: LOR_RARITIES, LOR_KEYWORDS: LOR_KEYWORDS, GD_KEYWORDS: GD_KEYWORDS, GD_RARITIES: GD_RARITIES, gdKeyword: gdKeyword, RB_KEYWORDS: RB_KEYWORDS, MTG_VERSIONS: MTG_VERSIONS, MTG_KEYWORDS: MTG_KEYWORDS, profile: profile, groupsOf: groupsOf,
    titleParts: titleParts, setCodes: setCodes, opVersion: opVersion, gsel: gsel, effPrice: effPrice
  };

  if (typeof module !== 'undefined' && module.exports) { module.exports = core; return; }
  root.SRFCore = core;

  /* ================================================================== */
  /* 2. ui                                                               */
  /* ================================================================== */

  var doc = root.document;
  if (!doc) return;

  function init() {
    var initStart = performance.now();
    var panel = doc.querySelector('[data-srf-results]');
    var grid = doc.getElementById('CollectionProductGrid');
    if (!panel || !grid || panel.dataset.srfReady) return;
    panel.dataset.srfReady = '1';

    var cfg = {};
    try { cfg = JSON.parse(doc.querySelector('[data-srf-config]').textContent); } catch (e) {}
    /* "filter": our own sidebar (the main MTG singles collection).
       "search": every other singles collection; only the card search, the theme's own sidebar
       and Shopify filters stay, and the search runs inside whatever those filters show. */
    var FILTER = cfg.mode !== 'search';
    /* mtg, op, pokemon, rb, lor, gd: which groups the sidebar has (core PROFILES); MTG uses the original ones */
    var GAME = cfg.game || '';
    var PROF = core.profile(GAME);
    var side = doc.querySelector('[data-srf]');
    if (!side) {
      if (FILTER) return;
      side = doc.createElement('aside'); /* stand-in, never attached: search mode has no sidebar */
    }

    var PAGE_SIZE = Math.max(1, parseInt(cfg.pageSize, 10) || 60); // same page size as the theme's grid
    var FIRST_ROWS = 2;       // rows of theme cards that must be ready before the grid is swapped
    /* Measured on the live store (2026-10-13, HTTP/3): all 30 MTG index pages at once take
       0.9 s against 2.6 s ten at a time, and 60 theme cards 0.9 s against 2.6 s, with no 429.
       Since 2026-10-17 the Singles collections include sold-out cards (MTG: 47 pages), so all
       pages are requested in one wave instead of a second wave after the first 30. */
    var CONCURRENCY = 64;     // index requests in flight
    var CARD_CONCURRENCY = 30; // theme-card requests in flight
    var INDEX_PAGE = 250;     // rows per index page (templates/collection.search-index.liquid)
    var FRESH_MS = 10 * 60 * 1000;      // a cached index this young is used as it is
    var CACHE_MS = 24 * 60 * 60 * 1000; // older than FRESH_MS: shown at once, then refreshed in the background
    var INDEX_VIEW = cfg.indexView || 'search-index';
    var TEXT_VIEW = cfg.textView || 'search-text';
    var ORDER_VIEW = cfg.orderView || 'search-order';
    var CARD_VIEW = cfg.cardView || 'srf-card';
    var PRODUCT_ROOT = cfg.productRoot || '/products/';

    function fromHash(hash) {
      var s = core.decodeState(hash, GAME);
      if (!FILTER) { var e = core.emptyState(GAME); e.q = s.q; e.sort = s.sort; e.page = s.page; s = e; }
      return s;
    }
    var state = fromHash(root.location.hash);
    var idx = { items: [], pages: 0, loaded: 0, total: cfg.total || 0, loading: null, done: false, failed: false, gen: 0, scope: '' };
    /* card text: loaded after the index, attached to the items, searched with name/set/type */
    var txt = { loading: null, done: false, failed: false, gen: -1, ms: null };
    /* "Best selling" order (handle -> rank), loaded the first time that sort is used with a filter */
    var best = { rank: null, loading: null, failed: false, gen: -1, ms: null };
    var view = { results: [], list: [], counts: null, setFilter: '', finds: {}, listHtml: {}, raf: 0, gridRaf: 0, slowTimer: 0, inputTimer: 0, key: '', painted: -1, scrollOnPaint: false, passSort: false, wantText: false };
    /* The theme's own grid (ul#main-collection-product-grid) and pagination. While a filter is on,
       the original cards are parked in a fragment and the results are drawn into the same list,
       so "View as", "Sort by", the card layout and the loading effect all stay the theme's. */
    var ella = { ul: null, kids: null, pag: null, ownPag: null, pageSort: '' };
    var wideMq = root.matchMedia ? root.matchMedia('(min-width: 1025px)') : { matches: true };
    var saveData = !!(root.navigator && navigator.connection && navigator.connection.saveData);
    var cards = new Map();    // handle -> server-rendered <li> html
    var pending = new Set();  // handles queued or being fetched
    var failedCards = new Set();
    var cardQueue = [];
    var cardActive = 0;
    var perf = { indexMs: null, parseMs: 0, lastApplyMs: null };
    root.SRFDebug = { game: GAME, state: function () { return state; }, idx: idx, txt: txt, perf: perf, view: view, ella: function () { return ella; }, mode: FILTER ? 'filter' : 'search' };

    var $ = function (sel, el) { return (el || doc).querySelector(sel); };
    var $$ = function (sel, el) { return Array.prototype.slice.call((el || doc).querySelectorAll(sel)); };
    function esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }
    function addDelims(num, dec, th, ds) {
      var p = num.toFixed(dec).split('.');
      p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, th);
      return p.join(ds);
    }
    function fmt(n) { return addDelims(n, 0, ',', '.'); }
    function money(cents) {
      var f = cfg.moneyFormat || '${{amount}}';
      var m = f.match(/\{\{\s*(\w+)\s*\}\}/);
      var n = (Number(cents) || 0) / 100;
      if (!m) return n.toFixed(2);
      var v;
      switch (m[1]) {
        case 'amount_no_decimals': v = addDelims(n, 0, ',', '.'); break;
        case 'amount_with_comma_separator': v = addDelims(n, 2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator': v = addDelims(n, 0, '.', ','); break;
        case 'amount_with_apostrophe_separator': v = addDelims(n, 2, "'", '.'); break;
        default: v = addDelims(n, 2, ',', '.');
      }
      return f.replace(m[0], v).replace(/<[^>]*>/g, '');
    }
    function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
    function toggle(arr, v) { var i = arr.indexOf(v); if (i === -1) arr.push(v); else arr.splice(i, 1); }
    function gToggle(id, v) { if (!state.g[id]) state.g[id] = []; toggle(state.g[id], v); }

    /* ---------- index and card text: fetch, cache ---------- */
    var path = root.location.pathname.replace(/\/+$/, '');

    /* Search mode runs inside the theme's Shopify filters, so they go along with every request
       (and into the cache key). Filter mode always loads the whole collection. */
    function scopeQuery() {
      if (FILTER) return '';
      var out = [];
      new URLSearchParams(root.location.search).forEach(function (v, k) {
        if (k.indexOf('filter.') === 0 && v !== '') out.push(encodeURIComponent(k) + '=' + encodeURIComponent(v));
      });
      return out.sort().join('&');
    }

    function idbOpen() {
      return new Promise(function (resolve) {
        try {
          var req = root.indexedDB.open('srf', 1);
          req.onupgradeneeded = function () { req.result.createObjectStore('idx'); };
          req.onsuccess = function () { resolve(req.result); };
          req.onerror = function () { resolve(null); };
        } catch (e) { resolve(null); }
      });
    }
    function cacheGet(key) {
      return idbOpen().then(function (db) {
        if (!db) return null;
        return new Promise(function (resolve) {
          try {
            var rq = db.transaction('idx', 'readonly').objectStore('idx').get(key);
            rq.onsuccess = function () {
              var d = rq.result;
              resolve(d && d.rows && Date.now() - d.t < CACHE_MS ? d : null); // caller checks d.t against FRESH_MS
            };
            rq.onerror = function () { resolve(null); };
          } catch (e) { resolve(null); }
        });
      });
    }
    function cacheSet(key, rows, total) {
      idbOpen().then(function (db) {
        if (!db) return;
        try { db.transaction('idx', 'readwrite').objectStore('idx').put({ t: Date.now(), rows: rows, total: total }, key); } catch (e) {}
      });
    }

    async function fetchPage(viewName, page, scope) {
      var url = path + '?view=' + viewName + '&page=' + page + (scope ? '&' + scope : '');
      for (var i = 0; i < 4; i++) {
        var r = await fetch(url, { credentials: 'same-origin' });
        if (r.status === 429) { await sleep(800 * (i + 1)); continue; }
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return JSON.parse(await r.text());
      }
      throw new Error('Too many requests');
    }

    /* All pages of one view. When the page count is known from the product count on the page
       (the whole collection, no Shopify filters), every page is requested at once; otherwise
       page 1 comes first (it says how many there are), then the rest. Pages past the end come
       back empty, and if the collection grew since the page was rendered, the extra pages are
       fetched once the first answers say so. */
    async function fetchAll(viewName, scope, onPage, alive, expectPages) {
      var raw = [];
      var pages = expectPages || 0;
      var total = 0;
      var next = 1;
      function take(d, n) {
        if (d.pages > pages) pages = d.pages;
        if (d.total) total = d.total;
        raw.push({ page: n, rows: d.items || [] });
        onPage(d.items || [], n, d);
      }
      if (!pages) {
        var first = await fetchPage(viewName, 1, scope);
        if (!alive()) return null;
        pages = first.pages || 1;
        take(first, 1);
        next = 2;
      }
      async function worker() {
        while (next <= pages && alive()) {
          var n = next++;
          var d = await fetchPage(viewName, n, scope);
          if (!alive()) return;
          take(d, n);
        }
      }
      var workers = [];
      for (var w = 0; w < Math.min(CONCURRENCY, Math.max(1, pages - next + 1)); w++) workers.push(worker());
      await Promise.all(workers);
      if (alive() && next <= pages) await Promise.all([worker()]);
      raw.sort(function (a, b) { return a.page - b.page; });
      return { raw: raw, total: total, pages: pages };
    }
    function expectedPages(scope) {
      return !scope && cfg.total ? Math.max(1, Math.ceil(cfg.total / INDEX_PAGE)) : 0;
    }

    function addRows(rows, pageNo) {
      var t0 = performance.now();
      for (var i = 0; i < rows.length; i++) idx.items.push(core.parseItem(rows[i], pageNo * 1000 + i, GAME));
      perf.parseMs += performance.now() - t0;
    }

    /* Start again (search mode, after the shopper changed a Shopify filter). */
    function resetIndex() {
      idx.gen++;
      idx.items = []; idx.pages = 0; idx.loaded = 0; idx.loading = null; idx.done = false; idx.failed = false;
      txt.loading = null; txt.done = false; txt.failed = false; txt.gen = -1;
      best.rank = null; best.loading = null; best.failed = false; best.gen = -1;
      view.key = ''; view.painted = -1;
    }

    /* Riftbound only: the card list that fills missing domains and gives signature cards their
       champion (core.rbEnrich). Loaded next to the index; if it arrives after the index, the cards
       already loaded are updated then. Without it the filter still works, from our own data. */
    var rbCards = { map: null, loading: null };
    function loadRbCards() {
      if (GAME !== 'rb' || !cfg.rbCards || rbCards.loading) return;
      rbCards.loading = fetch(cfg.rbCards, { credentials: 'omit' }).then(function (r) { return r.ok ? r.json() : null; }).then(function (m) {
        if (!m) return;
        rbCards.map = m;
        if (idx.done) { core.rbEnrich(idx.items, m); core.fillTwins(idx.items, GAME); schedule(); }
      }).catch(function () {});
    }

    function ensureIndex() {
      loadRbCards();
      var scope = scopeQuery();
      if (idx.scope !== scope) { if (idx.scope !== '' || idx.items.length || idx.loading) resetIndex(); idx.scope = scope; }
      if (idx.loading || idx.done) return idx.loading || Promise.resolve();
      var t0 = performance.now();
      var gen = idx.gen;
      var alive = function () { return idx.gen === gen; };
      var key = 'srf8:' + path + '?' + scope; // srf8: Gundam rows carry keywords (2026-10-30)
      idx.failed = false;
      side.classList.add('srf-loading');
      idx.loading = (async function () {
        var cached = await cacheGet(key);
        if (!alive()) return;
        var stale = false;
        if (cached) {
          cached.rows.forEach(function (p) { addRows(p.rows, p.page); });
          perf.fromCache = true;
          idx.total = cached.total;
          stale = Date.now() - cached.t >= FRESH_MS;
        } else {
          var res = await fetchAll(INDEX_VIEW, scope, function (rows, n, d) {
            if (d.pages) idx.pages = Math.max(idx.pages, d.pages);
            if (d.total) idx.total = d.total;
            addRows(rows, n); idx.loaded++; scheduleSlow();
          }, alive, expectedPages(scope));
          if (!res || !alive()) return;
          perf.fromCache = false;
          idx.total = res.total || idx.total;
          cacheSet(key, res.raw, idx.total);
        }
        idx.items.sort(function (a, b) { return a.order - b.order; });
        core.rbEnrich(idx.items, rbCards.map);
        core.fillTwins(idx.items, GAME);
        idx.done = true; idx.loading = null;
        side.classList.remove('srf-loading');
        clearTimeout(view.slowTimer); view.slowTimer = 0;
        perf.indexMs = Math.round(performance.now() - t0);
        schedule();
        if (view.wantText || core.hasQuery(state)) ensureText();
        if (stale) refreshIndex(key, scope, gen);
      })().catch(function () {
        if (!alive()) return;
        idx.failed = true; idx.loading = null; side.classList.remove('srf-loading'); schedule();
      });
      return idx.loading;
    }

    /* A cached index older than FRESH_MS is shown at once, then replaced by a fresh copy loaded
       in the background (prices and stock change during the day). The card text that was
       already attached is carried over. */
    function refreshIndex(key, scope, gen) {
      var alive = function () { return idx.gen === gen; };
      var fresh = [];
      var t0 = performance.now();
      fetchAll(INDEX_VIEW, scope, function (rows, n) {
        for (var i = 0; i < rows.length; i++) fresh.push(core.parseItem(rows[i], n * 1000 + i, GAME));
      }, alive, expectedPages(scope)).then(function (res) {
        if (!res || !alive()) return;
        fresh.sort(function (a, b) { return a.order - b.order; });
        core.rbEnrich(fresh, rbCards.map);
        core.fillTwins(fresh, GAME);
        if (txt.done && txt.rows) {
          var byHandle = new Map();
          fresh.forEach(function (it) { byHandle.set(it.handle, it); });
          txt.rows.forEach(function (r) { var it = byHandle.get(r[0]); if (it) core.setText(it, r[1]); });
        }
        if (best.rank) applyBest(fresh);
        idx.items = fresh;
        idx.total = res.total || idx.total;
        cacheSet(key, res.raw, idx.total);
        perf.refreshMs = Math.round(performance.now() - t0);
        schedule(); // the grid is only redrawn if the cards on the current page changed
      }).catch(function () {});
    }

    function applyBest(items) {
      for (var i = 0; i < items.length; i++) {
        var r = best.rank.get(items[i].handle);
        items[i].best = r == null ? Infinity : r; // not in the list yet (added since): last
      }
    }

    /* The best-selling order of the same cards: Shopify's own sort_by=best-selling, handles only.
       Kept for a day like the index (best selling changes slowly). If it cannot be loaded,
       "Best selling" falls back to the collection order. */
    function ensureBest() {
      if (!idx.done) return ensureIndex();
      if (best.gen === idx.gen && (best.loading || best.rank || best.failed)) return best.loading || Promise.resolve();
      var gen = idx.gen;
      var alive = function () { return idx.gen === gen; };
      var scope = idx.scope;
      var key = 'srf7b:' + path + '?' + scope;
      var t0 = performance.now();
      best.gen = gen; best.rank = null; best.failed = false;
      best.loading = (async function () {
        var cached = await cacheGet(key);
        if (!alive()) return;
        var raw = cached && cached.rows;
        if (!raw) {
          var res = await fetchAll(ORDER_VIEW, 'sort_by=best-selling' + (scope ? '&' + scope : ''), function () {}, alive, expectedPages(scope));
          if (!res || !alive()) return;
          raw = res.raw;
          cacheSet(key, raw, res.total);
        }
        var rank = new Map();
        var n = 0;
        raw.forEach(function (p) { p.rows.forEach(function (h) { if (!rank.has(h)) rank.set(h, n++); }); });
        best.rank = rank;
        applyBest(idx.items);
        best.loading = null; best.ms = Math.round(performance.now() - t0);
        schedule();
      })().catch(function () {
        if (!alive()) return;
        best.failed = true; best.loading = null; schedule();
      });
      return best.loading;
    }

    function applyText(rows) {
      txt.rows = rows;
      var byHandle = new Map();
      idx.items.forEach(function (it) { byHandle.set(it.handle, it); });
      rows.forEach(function (r) { var it = byHandle.get(r[0]); if (it) core.setText(it, r[1]); });
    }

    function ensureText() {
      if (!idx.done) { view.wantText = true; return ensureIndex(); }
      if (txt.gen === idx.gen && (txt.loading || txt.done || txt.failed)) return txt.loading || Promise.resolve();
      var gen = idx.gen;
      var alive = function () { return idx.gen === gen; };
      var scope = idx.scope;
      var key = 'srf4t:' + path + '?' + scope;
      var t0 = performance.now();
      txt.gen = gen; txt.done = false; txt.failed = false;
      txt.loading = (async function () {
        var cached = await cacheGet(key);
        if (!alive()) return;
        var all = [];
        if (cached) cached.rows.forEach(function (p) { all = all.concat(p.rows); });
        else {
          var res = await fetchAll(TEXT_VIEW, scope, function (rows) { all = all.concat(rows); }, alive, expectedPages(scope));
          if (!res || !alive()) return;
          cacheSet(key, res.raw, 0);
        }
        applyText(all);
        txt.done = true; txt.loading = null; txt.ms = Math.round(performance.now() - t0);
        if (core.hasQuery(state)) schedule();
        if (cached && Date.now() - cached.t >= FRESH_MS) {
          var fresh = [];
          fetchAll(TEXT_VIEW, scope, function (rows) { fresh = fresh.concat(rows); }, alive, expectedPages(scope)).then(function (r2) {
            if (!r2 || !alive()) return;
            applyText(fresh);
            cacheSet(key, r2.raw, 0);
            if (core.hasQuery(state)) schedule();
          }).catch(function () {});
        }
      })().catch(function () {
        if (!alive()) return;
        txt.failed = true; txt.loading = null; schedule();
      });
      return txt.loading;
    }

    /* The search box and the phone "Filter" button are in the snippet, so they are on the page
       before this script runs. Here only the result count, the chips and the "no results" line
       are added above the theme's toolbar. The cards go into the theme's own grid. */
    function buildPanel() {
      $('[data-srf-out]', panel).innerHTML =
        '<div class="srf-status"><p class="srf-count" data-srf-count aria-live="polite"></p>' +
        '<div class="srf-chips" data-srf-chips></div></div>' +
        '<p class="srf-none" data-srf-none hidden></p>';
    }

    /* ---------- rendering ---------- */
    /* requestAnimationFrame does not run in a background tab, and some embedded views pause it
       while still reporting "visible". A timer backs it up; whichever comes first draws. */
    function frame(fn) {
      var done = false;
      function run() { if (done) return; done = true; fn(); }
      if (!doc.hidden) root.requestAnimationFrame(run);
      setTimeout(run, doc.hidden ? 16 : 120);
      return 1;
    }
    function schedule() {
      if (view.raf) return;
      view.raf = frame(function () { view.raf = 0; render(); });
    }
    /* While index pages are still arriving, redraw at most four times a second. */
    function scheduleSlow() {
      if (view.slowTimer) return;
      view.slowTimer = setTimeout(function () { view.slowTimer = 0; schedule(); }, 250);
    }
    function scheduleGrid() {
      if (view.gridRaf) return;
      view.gridRaf = frame(function () { view.gridRaf = 0; if (core.isActive(state) && view.key) syncGrid(); });
    }
    /* Phones and tablets: the sidebar is the theme's slide-in drawer, opened by the theme's own
       body class (the same one, same animation and overlay as the filter drawer on the other
       collection pages). theme.js opens it from [data-sidebar] and closes it from the close
       button or a tap outside; this file follows the class, so every way in or out works. */
    var OPEN_CLASS = 'open-mobile-sidebar';
    function drawerOpen() { return FILTER && !wideMq.matches && doc.body.classList.contains(OPEN_CLASS); }
    function sideVisible() { return wideMq.matches || drawerOpen(); }

    /* Loading state while new results are on their way: the grid that is already there fades
       and a small spinner sits over it. Only the grid is covered; the page, the sidebar and the
       search box stay as they are. Nothing is shown when the answer comes within 150 ms. */
    function busyBox() { var ul = ellaUl(); return ul ? ul.parentNode : null; }
    function loader(on) {
      if (view.loaderOn === on) return;
      view.loaderOn = on;
      clearTimeout(view.busyTimer);
      if (!on) {
        var box = busyBox();
        if (box) { box.classList.remove('srf-busy'); box.removeAttribute('aria-busy'); }
        return;
      }
      view.busyTimer = setTimeout(function () {
        var box = busyBox();
        if (!view.loaderOn || !box) return;
        if (!box.querySelector(':scope > .srf-spin')) box.insertAdjacentHTML('afterbegin', '<div class="srf-spin" aria-hidden="true"><span></span></div>');
        box.classList.add('srf-busy');
        box.setAttribute('aria-busy', 'true');
      }, 150);
    }

    function chipList() {
      var chips = [];
      var label = {};
      core.COLOURS.forEach(function (c) { label[c.id] = c.label; });
      if (core.hasQuery(state)) chips.push({ k: 'q', t: '“' + state.q.trim() + '”' });
      var stockLabel = {};
      core.STOCK.forEach(function (x) { stockLabel[x.id] = x.label; });
      state.stock.forEach(function (s) { chips.push({ k: 'stock', v: s, t: stockLabel[s] || s }); });
      if (state.colours.length) {
        chips.push({ k: 'colours', t: 'Colour: ' + state.colours.map(function (c) { return label[c]; }).join(state.colourMode === 'all' ? ' + ' : ' or ') });
      }
      state.sets.forEach(function (s) { chips.push({ k: 'sets', v: s, t: setLabelOf(s) }); });
      state.rarities.forEach(function (s) { chips.push({ k: 'rarities', v: s, t: s }); });
      if (state.types.length) chips.push({ k: 'types', t: 'Type: ' + state.types.join(state.typeMode === 'all' ? ' + ' : ' or ') });
      if (state.mvs.length) chips.push({ k: 'mvs', t: 'Mana value: ' + state.mvs.join(', ') });
      if (PROF) PROF.groups.forEach(function (g) {
        var sel = core.gsel(state, g.id);
        if (!sel.length) return;
        var names = sel.map(function (v) { return (g.names && g.names[v]) || v; });
        if (g.mode && state.gm[g.id] === 'all' && sel.length > 1) { chips.push({ k: 'g:' + g.id, t: g.label + ': ' + names.join(' + ') }); return; }
        sel.forEach(function (v, i) { chips.push({ k: 'g:' + g.id, v: v, t: (g.chip || '') + names[i] }); });
      });
      var finLabel = {};
      (PROF ? PROF.finishes : core.FINISHES).forEach(function (x) { finLabel[x.id] = x.label; });
      state.fins.forEach(function (s) { chips.push({ k: 'fins', v: s, t: finLabel[s] || s }); });
      if (state.pmin != null || state.pmax != null) {
        chips.push({ k: 'price', t: 'Price: ' + (state.pmin != null ? money(state.pmin) : 'any') + ' to ' + (state.pmax != null ? money(state.pmax) : 'any') });
      }
      return chips;
    }

    function renderSidebar(counts) {
      var ready = !!counts;
      $$('[data-srf-colour]', side).forEach(function (b) {
        var on = state.colours.indexOf(b.dataset.srfColour) !== -1;
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        b.classList.toggle('is-empty', ready && !on && !counts.colour[b.dataset.srfColour]);
      });
      $$('[data-srf-mode]', side).forEach(function (b) {
        var cur = b.dataset.srfMode === 'colour' ? state.colourMode : state.typeMode;
        b.setAttribute('aria-pressed', b.dataset.v === cur ? 'true' : 'false');
      });
      var sel = { rarity: state.rarities, type: state.types, fin: state.fins, stock: state.stock };
      $$('[data-srf-opt]', side).forEach(function (box) {
        var g = box.dataset.srfOpt;
        if (g === 'set') return;
        var on = sel[g].indexOf(box.value) !== -1;
        box.checked = on;
        var n = ready ? (counts[g][box.value] || 0) : null;
        var row = box.closest('.srf-check');
        $('[data-srf-n]', row).textContent = n == null ? '' : fmt(n);
        row.classList.toggle('is-empty', ready && !on && !n);
      });
      $$('[data-srf-mv]', side).forEach(function (b) {
        var on = state.mvs.indexOf(b.dataset.srfMv) !== -1;
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        b.classList.toggle('is-empty', ready && !on && !counts.mv[b.dataset.srfMv]);
      });
      $$('[data-srf-price]', side).forEach(function (inp) {
        if (doc.activeElement === inp) return;
        var v = state[inp.dataset.srfPrice];
        inp.value = v == null ? '' : (v / 100).toFixed(2).replace(/\.00$/, '');
      });
      renderSets(counts);
      renderGroups(counts);
      var sum = {
        stock: state.stock.length, colour: state.colours.length, set: state.sets.length, rarity: state.rarities.length, type: state.types.length,
        mv: state.mvs.length, fin: state.fins.length, price: (state.pmin != null || state.pmax != null) ? 1 : 0
      };
      if (PROF) PROF.groups.forEach(function (g) { sum['g:' + g.id] = core.gsel(state, g.id).length; });
      $$('[data-srf-sum]', side).forEach(function (el) {
        var n = sum[el.dataset.srfSum];
        el.textContent = n ? String(n) : '';
        el.hidden = !n;
      });
      $$('[data-srf-q]').forEach(function (inp) {
        if (doc.activeElement !== inp && inp.value !== state.q) inp.value = state.q;
        var clear = $('[data-srf-qclear]', inp.parentNode);
        if (clear) clear.hidden = !inp.value;
      });
      var active = core.isActive(state);
      $$('[data-srf-clear]', side).forEach(function (b) { b.disabled = !active; });
    }

    /* A checkbox list built from the counts (set names, and for other games the rarities and
       the One Piece types). With a find box, only names containing the typed text are shown;
       chosen options always stay. */
    /* keep a set code on one line: "(OP-07)", not "(OP-" / "07)" on a phone. Only the code
       setLabelOf added: a set whose own name ends in brackets ("... (Super Pre-Release Edition)")
       wraps as normal text, or the long bracket ran past the list and gave it a sideways
       scrollbar (2026-10-27). */
    function labelHtml(name, label, labelOf) {
      if (labelOf !== setLabelOf) return esc(label);
      var c = view.setCodes && view.setCodes[name];
      return c && label === name + ' (' + c + ')' ? esc(name) + ' <span style="white-space:nowrap">(' + esc(c) + ')</span>' : esc(label);
    }

    function renderList(box, opt, names, n, chosen, find, noun, labelOf) {
      var needle = core.norm(find || '');
      /* also match with spaces and hyphens ignored, so "op01" finds "Romance Dawn (OP-01)" (card
         numbers are written OP01-001) and "st12" finds "ST-12" */
      var squeezed = needle.replace(/ /g, '');
      names = names.slice();
      chosen.forEach(function (v) { if (names.indexOf(v) === -1) names.push(v); });
      var html = '', shown = 0;
      names.forEach(function (name) {
        var label = labelOf ? labelOf(name) : name;
        if (needle) {
          var hay = core.norm(label);
          if (hay.indexOf(needle) === -1 && hay.replace(/ /g, '').indexOf(squeezed) === -1) return;
        }
        var on = chosen.indexOf(name) !== -1;
        shown++;
        html += '<label class="srf-check' + (!on && !n[name] ? ' is-empty' : '') + '"><input type="checkbox" class="srf-check__box" ' + opt + ' value="' + esc(name) + '"' + (on ? ' checked' : '') + '>' +
          '<span class="srf-check__label">' + labelHtml(name, label, labelOf) + '</span><span class="srf-check__n" data-srf-n>' + fmt(n[name] || 0) + '</span></label>';
      });
      if (!shown) html = '<p class="srf-note">No ' + noun + ' match' + (needle ? ' “' + esc(find) + '”' : '') + '.</p>';
      var key = opt;
      if (view.listHtml[key] !== html) { box.innerHTML = html; view.listHtml[key] = html; }
    }
    function alpha(a, b) { return a.toLowerCase() < b.toLowerCase() ? -1 : (a.toLowerCase() > b.toLowerCase() ? 1 : 0); }
    function byCount(n) { return function (a, b) { return (n[b] || 0) - (n[a] || 0) || alpha(a, b); }; }

    /* MTG, Riftbound and One Piece players also know sets by code: "Modern Horizons 3 (MH3)". The code comes
       from the card titles, worked out again only when more of the index has loaded. The set
       search matches the label, so "mh3" finds the set too. */
    function setLabelOf(name) {
      if (GAME !== 'mtg' && GAME !== 'rb' && GAME !== 'op' && GAME !== 'gd') return name;
      if (view.setCodesN !== idx.items.length) { view.setCodes = core.setCodes(idx.items, GAME); view.setCodesN = idx.items.length; }
      var c = view.setCodes[name];
      return c ? name + ' (' + c + ')' : name;
    }

    function renderSets(counts) {
      var box = $('[data-srf-setlist]', side);
      if (!box) return;
      if (!counts) {
        if (idx.failed) box.innerHTML = '<p class="srf-note">The set list could not load. Reload the page to try again.</p>';
        return;
      }
      /* no empty row for products without a Set line ("Customized Singles") */
      var setNames = Object.keys(counts.set).filter(Boolean).sort(alpha), setLabel = null;
      if (GAME === 'lor') {
        /* Lorcana players know sets by number: newest first ("14 · Hyperia City"), promos last */
        var setNo = {};
        idx.items.forEach(function (it) { if (it.set && (setNo[it.set] == null || it.setNo > setNo[it.set])) setNo[it.set] = it.setNo; });
        setNames.sort(function (a, b) { return (setNo[b] == null ? -1 : setNo[b]) - (setNo[a] == null ? -1 : setNo[a]) || alpha(a, b); });
        setLabel = function (name) { return setNo[name] > 0 ? setNo[name] + ' \u00b7 ' + name : name; };
      } else if (GAME === 'rb') {
        setNames.sort(function (a, b) { return core.rbSetRank(a) - core.rbSetRank(b) || alpha(a, b); });
        setLabel = setLabelOf;
      } else if (GAME === 'mtg' || GAME === 'op' || GAME === 'gd') {
        setLabel = setLabelOf;
      }
      renderList(box, 'data-srf-opt="set"', setNames, counts.set, state.sets, view.setFilter, 'sets', setLabel);
      /* rarities without a fixed list in the sidebar (Pokemon has more than 20) */
      var rbox = $('[data-srf-rarlist]', side);
      if (rbox) renderList(rbox, 'data-srf-opt="rarity"', Object.keys(counts.rarity).filter(Boolean).sort(byCount(counts.rarity)), counts.rarity, state.rarities, '', 'rarities');
    }

    /* Groups from the game's profile: buttons (colours, costs), fixed checkboxes, and lists. */
    function renderGroups(counts) {
      if (!PROF) return;
      var ready = !!counts;
      PROF.groups.forEach(function (g) {
        var sel = core.gsel(state, g.id);
        var n = ready ? counts.g[g.id] || {} : null;
        $$('[data-srf-g="' + g.id + '"]', side).forEach(function (b) {
          var on = sel.indexOf(b.dataset.v) !== -1;
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
          b.classList.toggle('is-empty', ready && !on && !n[b.dataset.v]);
        });
        $$('[data-srf-gmode="' + g.id + '"]', side).forEach(function (b) {
          b.setAttribute('aria-pressed', b.dataset.v === (state.gm[g.id] || 'any') ? 'true' : 'false');
        });
        $$('input[data-srf-gopt="' + g.id + '"]', side).forEach(function (box) {
          if (box.closest('[data-srf-glist]')) return;
          var on = sel.indexOf(box.value) !== -1;
          box.checked = on;
          var row = box.closest('.srf-check');
          var c = ready ? (n[box.value] || 0) : null;
          $('[data-srf-n]', row).textContent = c == null ? '' : fmt(c);
          row.classList.toggle('is-empty', ready && !on && !c);
        });
        var list = $('[data-srf-glist="' + g.id + '"]', side);
        if (list && ready) renderList(list, 'data-srf-gopt="' + g.id + '"', Object.keys(n).sort(byCount(n)), n, sel, view.finds[g.id], g.noun || 'types');
      });
    }

    /* ---------- the theme's grid, toolbar and pagination ---------- */
    function ellaUl() {
      var ul = doc.getElementById('main-collection-product-grid');
      return ul && ul.tagName === 'UL' ? ul : null;
    }
    function toolbar() { return grid.querySelector('toolbar-item'); }
    function sortItems() { var tb = toolbar(); return tb ? $$('[data-sort-by-item]', tb) : []; }
    function sortValue(item) { var t = item.querySelector('.text'); return t ? t.getAttribute('data-value') : ''; }

    function readPageSort() {
      var q = new URLSearchParams(root.location.search).get('sort_by');
      if (q && core.SORTS.indexOf(q) !== -1) return q;
      var on = sortItems().filter(function (i) { return i.classList.contains('is-active'); })[0];
      var v = on && sortValue(on);
      return v && core.SORTS.indexOf(v) !== -1 ? v : 'best-selling';
    }

    /* Show `value` as the chosen sort in the theme's dropdowns, the way toolbar.js does it. */
    function syncSortUI(value) {
      var tb = toolbar();
      if (!tb) return;
      $$('[data-sorting]', tb).forEach(function (box) {
        var hit = null;
        $$('[data-sort-by-item]', box).forEach(function (item) {
          var on = sortValue(item) === value;
          item.classList.toggle('is-active', on);
          if (on) hit = item;
        });
        var label = box.querySelector('.label-text');
        if (hit && label && !box.dataset.mobile) {
          var text = hit.querySelector('.text').textContent.trim();
          if (label.textContent !== text) label.textContent = text;
        }
      });
    }
    function closeSortMenus() {
      var tb = toolbar();
      if (!tb) return;
      $$('[data-toggle]', tb).forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      doc.body.classList.remove('toolbar-modal-open');
    }

    /* Hand the grid back to the theme when the last filter is removed. If the shopper picked a
       different sort while filtering, let the theme's own sort reload the grid in that order. */
    function release() {
      view.key = ''; view.painted = -1; view.list = [];
      loader(false);
      if (ella.ul) {
        ella.ul.innerHTML = '';
        ella.ul.appendChild(ella.kids);
        if (ella.pag) ella.pag.hidden = false;
        if (ella.ownPag) ella.ownPag.remove();
        ella.ul = null; ella.kids = null; ella.pag = null; ella.ownPag = null;
      }
      var want = state.sort;
      if (!want || want === ella.pageSort) return;
      syncSortUI(ella.pageSort);
      var tb = toolbar();
      var item = tb && $$('[data-sorting]:not([data-mobile]) [data-sort-by-item]', tb).filter(function (i) { return sortValue(i) === want; })[0];
      if (!item) { state.sort = ella.pageSort; return; }
      view.passSort = true;
      try { item.click(); } finally { view.passSort = false; }
      ella.pageSort = want;
      /* toolbar.js copies the option's innerText into the label, which is "" while the menu is
         closed, so the "Sort by" box went blank (2026-10-21). It also only updates the desktop list.
         Set the label and both lists from the option's textContent. */
      syncSortUI(want);
    }

    /* The theme can replace the whole grid after we took it over: its own sort reloads the
       grid by fetch, and that answer can land after a new filter was chosen (Clear all hands
       the sort to the theme, then the shopper picks a filter before the reload is back).
       The new grid is the theme's, in the new order; adopt it and draw the results again. */
    function gridReplaced() {
      if (!ella.ul || ella.ul.isConnected) return false;
      ella.ul = null; ella.kids = null; ella.pag = null; ella.ownPag = null;
      view.key = ''; view.painted = -1;
      return true;
    }

    function takeOver() {
      if (ella.ul) return true;
      var ul = ellaUl();
      if (!ul) return false;
      ella.ul = ul;
      ella.kids = doc.createDocumentFragment();
      while (ul.firstChild) ella.kids.appendChild(ul.firstChild);
      var box = ul.parentNode;
      ella.pag = box.querySelector('.pagination-wrapper');
      if (ella.pag) ella.pag.hidden = true;
      ella.ownPag = doc.createElement('div');
      ella.ownPag.className = 'pagination-wrapper text-center srf-pager';
      ella.ownPag.setAttribute('data-srf-pager', '');
      if (ella.pag) ella.pag.parentNode.insertBefore(ella.ownPag, ella.pag.nextSibling);
      else ul.parentNode.insertBefore(ella.ownPag, ul.nextSibling);
      return true;
    }

    function columns() {
      var ul = ella.ul || ellaUl();
      if (!ul) return 4;
      if (ul.classList.contains('productList')) return 2;
      var m = ul.className.match(/\bcolumn-(\d)\b/);
      return m ? parseInt(m[1], 10) : 4;
    }

    /* Only used when the theme card for a product cannot be fetched. Same classes as the
       theme card, so it has the same size; it links to the product page. */
    function fallbackCard(it) {
      var name = core.cardName(it.title);
      var img = it.image ? '<img src="' + esc(String(it.image).replace(/([?&])width=\d+/, '$1width=360')) + '" alt="' + esc(name) + '" width="744" height="1039" loading="lazy" class="motion-reduce sr-card-img">' : '';
      var ep = core.effPrice(it, state);
      var price = (ep.varies ? '<span class="text">from</span> ' : '') + esc(money(ep.price));
      return '<li class="product" data-srf-h="' + esc(it.handle) + '"><div class="product-item"><div class="card ajax-loaded">' +
        '<div class="card-product"><div class="card-product__wrapper"><div class="card-media card-media--portrait media--loading-effect" style="padding-bottom: 100%;">' + img +
        '<a class="card-link" href="' + esc(it.url) + '" title="' + esc(it.title) + '"></a></div></div></div>' +
        '<div class="card-information"><div class="card-information__wrapper text-left"><div class="wrapper-title-vendor card-title-ellipsis">' +
        '<a class="card-title link-underline card-title-ellipsis" href="' + esc(it.url) + '"><span class="text">' + esc(it.title) + '</span></a></div>' +
        '<div class="card-price"><div class="price"><dl><div class="price__regular"><dd class="price__last"><span class="price-item price-item--regular">' + price + '</span></dd></div></dl></div></div></div>' +
        '<div class="wrapper__card"><div class="card-action"><a class="button button-ATC" href="' + esc(it.url) + '">Choose options</a></div></div></div>' +
        '</div></div></li>';
    }

    function cardHtml(it) { return cards.get(it.handle) || fallbackCard(it); }

    /* With a Finish or Condition filter on, the theme card's price (its lowest in-stock variant)
       can belong to another printing or condition. Show the price of what was filtered instead.
       Every card in the grid is checked each time the grid is synced, not only new ones: when the
       filter changes but the results stay the same, nothing is repainted, and a card must still
       switch to (or back from) the filtered price. The theme's own price is kept on the card
       (_srfPrice) to put back. */
    function finishPrices() {
      if (!ella.ul) return;
      var byHandle = null;
      ella.ul.querySelectorAll('li[data-srf-h]').forEach(function (li) {
        var box = li.querySelector('.card-price .price');
        if (!box) return;
        if (!byHandle) { byHandle = {}; view.list.forEach(function (x) { byHandle[x.handle] = x; }); }
        var it = byHandle[li.getAttribute('data-srf-h')];
        var ep = it ? core.effPrice(it, state) : null;
        var want = ep && ep.byFinish && (ep.price !== it.price || ep.varies !== it.varies) ? ep : null;
        if (!want) {
          if (li._srfPrice != null) { box.className = li._srfPrice.cls; box.innerHTML = li._srfPrice.html; li._srfPrice = null; li.removeAttribute('data-srf-p'); }
          return;
        }
        var key = want.price + (want.varies ? '+' : '');
        if (li.getAttribute('data-srf-p') === key) return;
        if (li._srfPrice == null) li._srfPrice = { cls: box.className, html: box.innerHTML };
        box.classList.remove('price--on-sale', 'price--sold-out');
        box.innerHTML = '<dl><div class="price__regular"><dd class="price__last"><span class="price-item price-item--regular">' +
          (want.varies ? '<span class="text">from</span> ' : '') + esc(money(want.price)) + '</span></dd></div></dl>';
        li.setAttribute('data-srf-p', key);
      });
    }
    function cardReady(it) { return cards.has(it.handle) || failedCards.has(it.handle); }

    /* Results are the theme's real cards (templates/product.srf-card.liquid), one small request
       per product, a few at a time: Liquid cannot take a list of handles from the URL.
       The grid keeps showing what it had, under the theme's loading spinner, until the first two
       rows of the new page are ready. Then it is swapped in one go, and the remaining cards are
       added below in order as they arrive. Nothing already on screen moves or changes size. */
    function syncGrid() {
      var list = view.list;
      var ready = 0;
      while (ready < list.length && cardReady(list[ready])) ready++;
      if (view.painted < 0) {
        var need = Math.min(list.length, columns() * FIRST_ROWS);
        if (ready < need) { loader(!(drawerOpen() && !wideMq.matches)); return; }
        if (!takeOver()) { loader(false); return; }
        ella.ul.innerHTML = list.slice(0, ready).map(cardHtml).join('');
        view.painted = ready;
        renderPager();
        loader(false);
        afterCards();
        if (view.scrollOnPaint) {
          view.scrollOnPaint = false;
          var top = grid.getBoundingClientRect().top + root.pageYOffset - 80;
          if (root.pageYOffset > top) root.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        }
      } else if (ready > view.painted) {
        ella.ul.insertAdjacentHTML('beforeend', list.slice(view.painted, ready).map(cardHtml).join(''));
        view.painted = ready;
        afterCards();
      }
      if (view.painted >= 0) finishPrices();
    }

    function pagerParts(page, pages) {
      var want = [1, pages];
      for (var i = page - 2; i <= page + 2; i++) if (i >= 1 && i <= pages) want.push(i);
      want = want.filter(function (n, i, a) { return a.indexOf(n) === i; }).sort(function (a, b) { return a - b; });
      var out = [], prev = 0;
      want.forEach(function (n) { if (n - prev > 1) out.push(0); out.push(n); prev = n; });
      return out;
    }

    /* Same markup as snippets/pagination-listing.liquid, so the theme's CSS styles it. */
    function renderPager() {
      if (!ella.ownPag) return;
      var total = view.results.length;
      var pages = Math.ceil(total / PAGE_SIZE);
      if (pages <= 1) { ella.ownPag.innerHTML = ''; ella.ownPag.hidden = true; return; }
      ella.ownPag.hidden = false;
      var page = state.page;
      var start = (page - 1) * PAGE_SIZE + 1;
      var end = Math.min(page * PAGE_SIZE, total);
      var progress = page < pages ? Math.floor(end * 100 / total) + '%' : '100%';
      var nav = ella.pag && ella.pag.querySelector('nav');
      var navClass = nav ? nav.className : 'pagination style--2 text-center';
      var origNext = ella.pag && ella.pag.querySelector('.pagination__item--next');
      var arrow = origNext ? origNext.innerHTML : '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-caret" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z"></path></svg>';
      function href(n) { var s = Object.assign({}, state, { page: n }); return esc(root.location.pathname + root.location.search + core.encodeState(s)); }
      var h = '<nav class="' + esc(navClass) + '" role="navigation" aria-label="Pagination">' +
        '<div class="pagination-page-item pagination-page-total"><span>Showing</span> <span data-total-start>' + fmt(start) + '</span> <span>-</span><span data-total-end>' + fmt(end) + '</span><span> of ' + fmt(total) + ' total</span>' +
        '<div class="pagination-total-progress"><span style="width: ' + progress + '" class="pagination-total-item"></span></div></div>' +
        '<ul class="pagination__list list-unstyled" role="list">';
      if (page > 1) h += '<li class="pagination-arrow"><a href="' + href(page - 1) + '" data-srf-page="' + (page - 1) + '" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" aria-label="Previous">' + arrow + '</a></li>';
      pagerParts(page, pages).forEach(function (n) {
        if (!n) h += '<li class="pagination-num"><span class="pagination__item">…</span></li>';
        else if (n === page) h += '<li class="pagination-num"><span class="pagination__item pagination__item--current" aria-current="page" aria-label="Page ' + n + '">' + n + '</span></li>';
        else h += '<li class="pagination-num"><a href="' + href(n) + '" data-srf-page="' + n + '" class="pagination__item link" aria-label="Page ' + n + '">' + n + '</a></li>';
      });
      if (page < pages) h += '<li class="pagination-arrow"><a href="' + href(page + 1) + '" data-srf-page="' + (page + 1) + '" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" aria-label="Next">' + arrow + '</a></li>';
      h += '</ul></nav>';
      ella.ownPag.innerHTML = h;
    }

    function fetchCards(handles) {
      var want = new Set(handles);
      cardQueue = cardQueue.filter(function (h) {
        if (want.has(h)) return true;
        pending.delete(h);
        return false;
      });
      handles.forEach(function (h) {
        if (cards.has(h) || pending.has(h) || failedCards.has(h)) return;
        pending.add(h); cardQueue.push(h);
      });
      pumpCards();
    }

    function pumpCards() {
      while (cardActive < CARD_CONCURRENCY && cardQueue.length) loadCard(cardQueue.shift(), 0);
    }

    function toCard(html, handle) {
      var tpl = doc.createElement('template');
      tpl.innerHTML = html;
      var el = tpl.content.firstElementChild;
      if (!el) return null;
      var li = el;
      if (el.tagName !== 'LI') {
        li = doc.createElement('li');
        li.className = 'product';
        if (el.classList.contains('product')) while (el.firstChild) li.appendChild(el.firstChild);
        else li.appendChild(el);
      }
      if (!li.firstElementChild) return null;
      li.setAttribute('data-srf-h', handle);
      return li;
    }

    function loadCard(handle, attempt) {
      cardActive++;
      var retry = false;
      fetch(PRODUCT_ROOT + encodeURIComponent(handle) + '?view=' + CARD_VIEW, { credentials: 'same-origin' }).then(function (r) {
        if (r.status === 429 && attempt < 2) { retry = true; return sleep(900 * (attempt + 1)).then(function () { return null; }); }
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      }).then(function (html) {
        if (html == null) return;
        var li = toCard(html, handle);
        if (!li) throw new Error('empty card');
        cards.set(handle, li.outerHTML);
      }).catch(function () {
        failedCards.add(handle); /* a plain card with the same layout is used instead */
      }).then(function () {
        cardActive--;
        if (retry) { loadCard(handle, attempt + 1); return; }
        pending.delete(handle);
        pumpCards();
        scheduleGrid();
      });
    }

    function afterCards() {
      try {
        if (root.wishlist && root.wishlist.show && root.halo && root.halo.setLocalStorageProductForWishlist) root.halo.setLocalStorageProductForWishlist();
      } catch (e) {}
      try {
        if (root.halo && root.halo.checkNeedToConvertCurrency && root.halo.checkNeedToConvertCurrency() && root.Currency && root.jQuery) {
          root.Currency.convertAll(root.shop_currency, root.jQuery('#currencies .active').attr('data-currency'), 'span.money', 'money_format');
        }
      } catch (e) {}
    }

    function render() {
      gridReplaced();
      /* search mode: the shopper changed a Shopify filter while searching -> new index */
      if (!FILTER && core.isActive(state) && scopeQuery() !== idx.scope) ensureIndex();
      var active = core.isActive(state);
      var ready = idx.items.length > 0;
      var counts = null;
      if (ready) {
        var t0 = performance.now();
        view.results = active ? core.apply(idx.items, state) : [];
        counts = FILTER ? core.facets(idx.items, state) : null;
        perf.lastApplyMs = Math.round((performance.now() - t0) * 10) / 10;
      }
      view.counts = counts;
      /* On a phone the sidebar is hidden until the drawer opens, and the results are behind
         the drawer while it is open. Skip whichever part cannot be seen; it is drawn when shown. */
      if (FILTER && sideVisible()) renderSidebar(counts);

      var openN = $('[data-srf-open-n]', panel);
      var groups = core.activeGroupCount(state);
      if (openN) { openN.textContent = groups ? String(groups) : ''; openN.hidden = !groups; }
      var apply = $('[data-srf-apply]', side);
      if (apply) apply.textContent = active && ready && idx.done ? 'Show ' + fmt(view.results.length) + ' result' + (view.results.length === 1 ? '' : 's') : 'Show results';

      if (drawerOpen() && !wideMq.matches) { loader(false); return; }

      var out = $('[data-srf-out]', panel);
      out.hidden = !active;
      if (!active) { release(); return; }

      var countEl = $('[data-srf-count]', panel);
      var usable = idx.done || (idx.failed && ready);
      if (state.sort === 'best-selling' && idx.done && !best.failed && !(best.rank && best.gen === idx.gen)) {
        ensureBest();
        usable = false; // keep "Loading cards…" until the order is there, so the grid does not reshuffle
      }
      if (idx.failed && !ready) {
        countEl.innerHTML = 'Search and filters are not available right now. <a href="' + esc((cfg.searchUrl || '/search') + '?q=' + encodeURIComponent(state.q || '') + '&type=product') + '">Search the whole store</a>.';
      } else if (!usable) {
        countEl.innerHTML = 'Loading cards…';
      } else {
        countEl.innerHTML = '<strong>' + fmt(view.results.length) + '</strong> result' + (view.results.length === 1 ? '' : 's') +
          (core.hasQuery(state) && txt.loading ? ' <span class="srf-count__sub">· checking card text…</span>' : '');
      }

      var chips = chipList();
      $('[data-srf-chips]', panel).innerHTML = chips.map(function (c) {
        return '<button type="button" class="srf-chip" data-srf-chip="' + c.k + '"' + (c.v != null ? ' data-v="' + esc(c.v) + '"' : '') + '>' +
          '<span>' + esc(c.t) + '</span><svg aria-hidden="true" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18"></path></svg>' +
          '<span class="visually-hidden">Remove</span></button>';
      }).join('') + (chips.length ? '<button type="button" class="srf-chips__clear" data-srf-clear>Clear all</button>' : '');

      syncSortUI(state.sort);

      var none = $('[data-srf-none]', panel);
      if (!usable) { none.hidden = true; loader(!idx.failed); return; }

      var pages = Math.max(1, Math.ceil(view.results.length / PAGE_SIZE));
      if (state.page > pages) { state.page = pages; writeHash(); }
      var list = view.results.slice((state.page - 1) * PAGE_SIZE, state.page * PAGE_SIZE);
      var key = state.page + ':' + list.map(function (it) { return it.handle; }).join(',');
      if (key !== view.key) {
        view.key = key; view.list = list; view.painted = -1;
        /* Theme cards are only fetched once the index is complete, so cards are not requested
           for a page that is still changing. */
        fetchCards(list.map(function (it) { return it.handle; }));
      }
      syncGrid();
      if (view.painted >= 0) renderPager();

      if (!view.results.length) {
        none.hidden = false;
        var onlyQuery = core.activeGroupCount(state) === 0;
        var storeLink = core.hasQuery(state) ? '<a href="' + esc((cfg.searchUrl || '/search') + '?q=' + encodeURIComponent(state.q) + '&type=product') + '">search the whole store</a>' : '';
        none.innerHTML = onlyQuery
          ? 'No cards match “' + esc(state.q.trim()) + '”. <button type="button" class="srf-link" data-srf-clear>Clear the search</button>' + (storeLink ? ' or ' + storeLink + '.' : '.')
          : 'No cards match these filters. <button type="button" class="srf-link" data-srf-clear>Clear all filters</button>' + (storeLink ? ' or ' + storeLink + '.' : '.');
      } else none.hidden = true;
    }

    /* ---------- state changes ---------- */
    function writeHash(push) {
      var url = root.location.pathname + root.location.search + core.encodeState(state);
      try {
        if (push) root.history.pushState(root.history.state, '', url);
        else root.history.replaceState(root.history.state, '', url);
      } catch (e) {}
    }
    /* A search needs the card text as well as the index (the text also loads when the box gets
       focus, but a query can arrive without that: a back/forward step, autofill, voice input). */
    function load() {
      if (core.hasQuery(state)) ensureText();
      else if (core.isActive(state)) ensureIndex();
    }
    function changed(keepPage) {
      if (!keepPage) state.page = 1;
      writeHash();
      load();
      schedule();
    }
    function clearAll() {
      var sort = state.sort;
      state = core.emptyState(GAME);
      state.sort = sort;
      view.setFilter = '';
      view.finds = {};
      var sf = $('[data-srf-setfind]', side); if (sf) sf.value = '';
      $$('[data-srf-gfind]', side).forEach(function (i) { i.value = ''; });
      $$('[data-srf-q]').forEach(function (i) { i.value = ''; });
      changed();
    }
    function setQuery(q, from) {
      state.q = q;
      $$('[data-srf-q]').forEach(function (i) { if (i !== from) i.value = q; });
      changed();
    }

    /* ---------- mobile drawer ---------- */
    var lastFocus = null;
    var wasOpen = false;
    var scrollOnClose = false;
    function openDrawer() { doc.body.classList.add(OPEN_CLASS); }
    function closeDrawer(scroll) {
      scrollOnClose = !!scroll;
      doc.body.classList.remove(OPEN_CLASS);
    }
    function drawerChanged() {
      var open = drawerOpen();
      if (open === wasOpen) return;
      wasOpen = open;
      if (open) {
        lastFocus = doc.activeElement;
        ensureIndex();
        render();
        var c = side.querySelector('.halo-sidebar-close'); if (c) c.focus({ preventScroll: true });
        return;
      }
      render();
      if (lastFocus && lastFocus.focus && doc.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
      if (scrollOnClose && core.isActive(state)) {
        root.scrollTo({ top: panel.getBoundingClientRect().top + root.pageYOffset - 12, behavior: 'smooth' });
      }
      scrollOnClose = false;
    }

    /* ---------- events ---------- */
    function bind() {
      doc.addEventListener('input', function (e) {
        var t = e.target;
        if (t.matches && t.matches('[data-srf-q]')) {
          clearTimeout(view.inputTimer);
          var v = t.value;
          $('[data-srf-qclear]', t.parentNode).hidden = !v;
          view.inputTimer = setTimeout(function () { setQuery(v, t); }, 140);
        } else if (t.matches && t.matches('[data-srf-setfind]')) {
          view.setFilter = t.value; schedule();
        } else if (t.matches && t.matches('[data-srf-gfind]')) {
          view.finds[t.dataset.srfGfind] = t.value; schedule();
        }
      });
      doc.addEventListener('focusin', function (e) {
        if (!e.target.matches) return;
        if (e.target.matches('[data-srf-q]')) { view.wantText = true; ensureText(); }
        else if (e.target.matches('[data-srf-setfind],[data-srf-gfind]')) ensureIndex();
      });
      doc.addEventListener('keydown', function (e) {
        var t = e.target;
        if (e.key === 'Escape' && drawerOpen()) { closeDrawer(false); return; }
        if (!(t.matches && t.matches('[data-srf-q]'))) return;
        if (e.key === 'Escape') { t.value = ''; clearTimeout(view.inputTimer); setQuery('', null); }
        if (e.key === 'Enter') { e.preventDefault(); clearTimeout(view.inputTimer); setQuery(t.value, t); t.blur(); }
      });
      doc.addEventListener('change', function (e) {
        var t = e.target;
        if (!t.matches) return;
        if (t.matches('[data-srf-opt]')) {
          var map = { set: 'sets', rarity: 'rarities', type: 'types', fin: 'fins', stock: 'stock' };
          toggle(state[map[t.dataset.srfOpt]], t.value);
          changed();
        } else if (t.matches('[data-srf-gopt]')) {
          gToggle(t.dataset.srfGopt, t.value);
          changed();
        } else if (t.matches('[data-srf-price]')) {
          var n = parseFloat(t.value);
          state[t.dataset.srfPrice] = isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
          changed();
        }
      });
      /* The theme's "Sort by". Without a filter the theme sorts the grid itself (we only note the
         choice). With a filter on, the same dropdown sorts the filtered results instead of
         reloading the page. Capture phase, so this runs before toolbar.js. */
      doc.addEventListener('click', function (e) {
        var item = e.target.closest ? e.target.closest('[data-sort-by-item]') : null;
        if (!item || view.passSort || !grid.contains(item)) return;
        var v = sortValue(item);
        if (core.SORTS.indexOf(v) === -1) return;
        if (!core.isActive(state)) { state.sort = v; ella.pageSort = v; return; }
        e.preventDefault();
        e.stopImmediatePropagation();
        closeSortMenus();
        if (v === state.sort) return;
        state.sort = v;
        syncSortUI(v);
        changed();
      }, true);
      doc.addEventListener('click', function (e) {
        var t = e.target.closest ? e.target.closest('[data-srf-colour],[data-srf-mode],[data-srf-mv],[data-srf-g],[data-srf-gmode],[data-srf-clear],[data-srf-chip],[data-srf-page],[data-srf-open],[data-srf-close],[data-close-sidebar],[data-srf-qclear]') : null;
        if (!t) return;
        if (!side.contains(t) && !panel.contains(t) && !(ella.ownPag && ella.ownPag.contains(t))) return;
        if (t.hasAttribute('data-srf-colour')) { toggle(state.colours, t.dataset.srfColour); changed(); }
        else if (t.hasAttribute('data-srf-mode')) {
          if (t.dataset.srfMode === 'colour') state.colourMode = t.dataset.v; else state.typeMode = t.dataset.v;
          changed();
        }
        else if (t.hasAttribute('data-srf-mv')) { toggle(state.mvs, t.dataset.srfMv); changed(); }
        else if (t.hasAttribute('data-srf-g')) { gToggle(t.dataset.srfG, t.dataset.v); changed(); }
        else if (t.hasAttribute('data-srf-gmode')) { state.gm[t.dataset.srfGmode] = t.dataset.v; changed(); }
        else if (t.hasAttribute('data-srf-clear')) { clearAll(); }
        else if (t.hasAttribute('data-srf-chip')) {
          var k = t.dataset.srfChip;
          if (k === 'q') { setQuery('', null); return; }
          if (k === 'price') { state.pmin = null; state.pmax = null; }
          else if (k.indexOf('g:') === 0) { if (t.dataset.v != null) gToggle(k.slice(2), t.dataset.v); else state.g[k.slice(2)] = []; }
          else if (t.dataset.v != null) toggle(state[k], t.dataset.v);
          else state[k] = [];
          changed();
        }
        else if (t.hasAttribute('data-srf-page')) {
          e.preventDefault();
          state.page = parseInt(t.dataset.srfPage, 10) || 1;
          view.scrollOnPaint = true;
          writeHash(true);
          schedule();
        }
        else if (t.hasAttribute('data-srf-open')) { openDrawer(); }
        else if (t.hasAttribute('data-srf-close')) { closeDrawer(t.hasAttribute('data-srf-apply')); }
        else if (t.hasAttribute('data-close-sidebar')) { closeDrawer(false); }
        else if (t.hasAttribute('data-srf-qclear')) {
          var inp = $('[data-srf-q]', t.parentNode);
          inp.value = ''; clearTimeout(view.inputTimer); setQuery('', null); inp.focus();
        }
      });
      if (wideMq.addEventListener) wideMq.addEventListener('change', function () {
        if (wideMq.matches) doc.body.classList.remove(OPEN_CLASS);
        drawerChanged();
        schedule();
      });
      if (root.MutationObserver) {
        new root.MutationObserver(drawerChanged).observe(doc.body, { attributes: true, attributeFilter: ['class'] });
        new root.MutationObserver(function () {
          if (gridReplaced()) schedule();
          else if (!FILTER && core.isActive(state) && scopeQuery() !== idx.scope) schedule();
        }).observe(grid, { childList: true, subtree: true });
      }
      function fromUrl() {
        var next = fromHash(root.location.hash);
        if (!next.sort) next.sort = state.sort || ella.pageSort;
        if (core.encodeState(next) === core.encodeState(state)) return;
        state = next;
        view.scrollOnPaint = true;
        load();
        schedule();
      }
      root.addEventListener('hashchange', fromUrl);
      root.addEventListener('popstate', fromUrl);
    }

    /* ---------- start ---------- */
    buildPanel();
    bind();
    ella.pageSort = readPageSort();
    if (!state.sort) state.sort = ella.pageSort;
    render();
    perf.initMs = Math.round((performance.now() - initStart) * 10) / 10;

    if (core.isActive(state)) ensureIndex();
    else {
      /* Filter mode on desktop shows the sidebar straight away, so warm the index when the
         browser is idle. Phones, and search mode, wait until the shopper uses the search box
         (or opens the drawer). The card text always waits for the search box. */
      var wide = wideMq.matches;
      if (FILTER && wide && saveData) side.classList.remove('srf-loading');
      if (FILTER && wide && !saveData) {
        var warm = function () { ensureIndex(); };
        var start = function () { if (root.requestIdleCallback) root.requestIdleCallback(warm, { timeout: 4000 }); else setTimeout(warm, 1500); };
        if (doc.readyState === 'complete') start(); else root.addEventListener('load', start, { once: true });
      }
    }
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})(typeof window !== 'undefined' ? window : this);
